/* eslint-disable @atlaskit/ui-styling-standard/no-important-styles, @atlaskit/ui-styling-standard/no-unsafe-selectors */
/* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values */
/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-imports, @atlaskit/ui-styling-standard/use-compiled -- emotion jsx pragma; go/DSP-18766
import { css, jsx, type SerializedStyles } from '@emotion/react'; // oxlint-ignore @typescript-eslint/consistent-type-imports -- classic @jsx jsx factory + jsx.JSX.Element types
import { token } from '@atlaskit/tokens';
import type { RendererWrapperProps } from './index';
import { FullPagePadding } from './style';
import { fg } from '@atlaskit/platform-feature-flags';
import { RendererCssClassName } from '../../consts';
import {
	akEditorCalculatedWideLayoutWidth,
	akEditorCalculatedWideLayoutWidthSmallViewport,
	akEditorFullPageNarrowBreakout,
	akEditorGutterPaddingReduced,
	akEditorDefaultLayoutWidth,
	akEditorFullWidthLayoutWidth,
	akEditorMaxWidthLayoutWidth,
	akEditorGutterPadding,
	akEditorLineHeight,
	akEditorSelectedNodeClassName,
	akEditorShadowZIndex,
	akEditorStickyHeaderZIndex,
	akEditorTableCellMinWidth,
	akEditorTableNumberColumnWidth,
	blockNodesVerticalMargin,
	scaledBlockNodesVerticalMargin,
	gridMediumMaxWidth,
	akEditorFullPageDefaultFontSize,
	akEditorFullPageDenseFontSize,
} from '@atlaskit/editor-shared-styles';
import { INLINE_IMAGE_WRAPPER_CLASS_NAME } from '@atlaskit/editor-common/media-inline';
import { HeadingAnchorWrapperClassName } from '../../react/nodes/heading-anchor';
import {
	CodeBlockSharedCssClassName,
	DateSharedCssClassName,
	listItemCounterPadding,
	richMediaClassName,
	SmartCardSharedCssClassName,
	tableCellBorderWidth,
	tableCellMinWidth,
	tableCellPadding,
	tableMarginTop,
	TableSharedCssClassName,
	TaskDecisionSharedCssClassName,
} from '@atlaskit/editor-common/styles';
import { bulletListSelector, orderedListSelector } from '@atlaskit/adf-schema';
import { shadowClassNames, shadowObserverClassNames } from '@atlaskit/editor-common/ui';
import { getBrowserInfo } from '@atlaskit/editor-common/browser';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { isStickyScrollbarEnabled, isTableResizingEnabled } from '../../react/nodes/table';
import { SORTABLE_COLUMN_ICON_CLASSNAME } from '@atlaskit/editor-common/table';
import { LightWeightCodeBlockCssClassName } from '../../react/nodes/codeBlock/components/lightWeightCodeBlock';
import { editorUGCTokensRefreshed } from '@atlaskit/editor-common/ugc-tokens';
import { getBaseFontSize } from './get-base-font-size';
import {
	EmojiSharedCssClassName,
	defaultEmojiHeight,
	defaultDenseEmojiHeight,
	defaultInlineEmojiHeight,
	scaledEmojiHeightH1,
	scaledEmojiHeightH2,
	scaledEmojiHeightH3,
	scaledEmojiHeightH4,
	denseEmojiHeightH1,
	denseEmojiHeightH2,
	denseEmojiHeightH3,
	denseEmojiHeightH4,
} from '@atlaskit/editor-common/emoji';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import {
	BodiedSyncBlockSharedCssClassName,
	SyncBlockSharedCssClassName,
} from '@atlaskit/editor-common/sync-block';

import { COLLAPSED_CONTENT_OWNERS_ATTRIBUTE } from '../collapsible-headings-dom';

const wrappedMediaBreakoutPoint = 410;
const TELEPOINTER_ID = 'ai-streaming-telepointer';
const tableShadowWidth = 32;
const LAYOUT_BREAKPOINT_RENDERER = 629;
const REMIX_BLOCK_HIGHLIGHT_CLASS_NAME = 'remix-block-highlight';
// originally defined from packages/editor/editor-plugin-table/src/ui/common-styles.ts
// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
// TODO: tableRowHeight can be moved into `@atlaskit/editor-common/table`
const tableRowHeight = 44;

const isBackgroundClipBrowserFixNeeded = () => {
	const browser = getBrowserInfo();
	return browser.isGecko || browser.isIE || (browser.isMac && browser.isChrome);
};

const baseFontStyle = css({
	font: editorUGCTokensRefreshed['editor.font.body'],
});

const fontSizeStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.fabric-editor-font-size': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		"&[data-font-size='small']": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			font: 'var(--ak-renderer-editor-font-small-text)',
		},
	},

	// Apply font-size to the ::marker pseudo-element of list items that have a font-size mark.
	// Targeting ::marker directly avoids setting font on the <li> itself, which would cascade
	// into nested lists and compound the sizing at each nesting level.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	"li:has(> .fabric-editor-font-size[data-font-size='small'])::marker": {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: 'var(--ak-renderer-editor-font-small-text)',
	},
});

const originalBaseFontLineHeight = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '1.5rem',
});

// styles are copied from ./style.tsx
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles, @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/design-system/consistent-css-prop-usage
const baseStyles = css({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: 'var(--ak-renderer-base-font-size)',
	color: token('color.text'),

	'--ak-editor--full-width-layout-width': `${akEditorFullWidthLayoutWidth}px`,

	[`.${RendererCssClassName.DOCUMENT}::after`]: {
		// we add a clearfix after ak-renderer-document in order to
		// contain internal floats (such as media images that are "wrap-left")
		// to just the renderer (and not spill outside of it)
		content: '""',
		visibility: 'hidden',
		display: 'block',
		height: 0,
		clear: 'both',
	},

	[`.${RendererCssClassName.DOCUMENT}`]: {
		[`> [${COLLAPSED_CONTENT_OWNERS_ATTRIBUTE}][hidden='until-found']`]: {
			display: 'block',
			minBlockSize: 0,
			marginBlock: 0,
			paddingBlock: 0,
			borderBlockWidth: 0,
		},

		// p, h3, and action items
		[`.${INLINE_IMAGE_WRAPPER_CLASS_NAME}`]: {
			height: '22px',
			transform: `translateY(-2px)`,
		},

		h1: {
			[`> .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .${INLINE_IMAGE_WRAPPER_CLASS_NAME}`]: {
				height: '36px',
				transform: `translateY(-3px)`,
			},
		},
		h2: {
			[`> .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .${INLINE_IMAGE_WRAPPER_CLASS_NAME}`]: {
				height: '31px',
				transform: `translateY(-3px)`,
			},
		},
		h3: {
			[`> .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .${INLINE_IMAGE_WRAPPER_CLASS_NAME}`]: {
				height: '25px',
				transform: `translateY(-2px)`,
			},
		},
		h4: {
			[`> .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .${INLINE_IMAGE_WRAPPER_CLASS_NAME}`]: {
				height: '23px',
				transform: `translateY(-2px)`,
			},
		},
		h5: {
			[`> .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .${INLINE_IMAGE_WRAPPER_CLASS_NAME}`]: {
				height: '20px',
				transform: `translateY(-2px)`,
			},
		},
		h6: {
			[`> .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .mediaInlineView-content-wrap > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > .${INLINE_IMAGE_WRAPPER_CLASS_NAME},
  > :is(a, span[data-mark-type='border']) .${INLINE_IMAGE_WRAPPER_CLASS_NAME}`]: {
				height: '18px',
				transform: `translateY(-2px)`,
			},
		},
	},

	[`& span.akActionMark`]: {
		color: token('color.link'),
		textDecoration: 'none',
		cursor: 'pointer',
		'&:hover': {
			color: token('color.link'),
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},

	'& span[data-placeholder]': {
		color: token('color.text.subtlest'),
	},
});

const headingAnchorStylesDuplicateAnchor = css({
	'& h1, & h2, & h3, & h4, & h5, & h6': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			position: 'absolute',
			marginLeft: token('space.075'),

			button: {
				paddingLeft: 0,
				paddingRight: 0,
			},

			/**
			 * Adds the visibility of the button when in focus through keyboard navigation.
			 */
			'button:focus': {
				opacity: 1,
				transform: 'none !important',
			},
		},

		[`@media (hover: hover) and (pointer: fine)`]: {
			[`.${HeadingAnchorWrapperClassName}`]: {
				'> button': {
					opacity: 0,
					transform: 'translate(-8px, 0px)',
					transitionProperty: 'opacity, transform',
					transitionDuration: `${token('motion.duration.medium', '0.2s')}, ${token('motion.duration.medium', '0.2s')}`,
					transitionTimingFunction: `${token('motion.easing.out.practical', 'ease')}, ${token('motion.easing.out.practical', 'ease')}`,
					transitionDelay: '0s, 0s',
				},
			},

			'&:hover': {
				[`.${HeadingAnchorWrapperClassName} > button`]: {
					opacity: 1,
					transform: 'none !important',
				},
			},
		},
	},
	'& h1': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${28 / 24}em`,
		},
	},
	'& h2': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${24 / 20}em`,
		},
	},
	'& h3': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${20 / 16}em`,
		},
	},
	'& h4': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${16 / 14}em`,
		},
	},
	'& h5': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${16 / 12}em`,
		},
	},
	'& h6': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${16 / 11}em`,
		},
	},
});

const headingAnchorStyles = css({
	'& .renderer-heading-wrapper': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			marginLeft: token('space.075'),

			button: {
				paddingLeft: 0,
				paddingRight: 0,
			},

			/**
			 * Adds the visibility of the button when in focus through keyboard navigation.
			 */
			'button:focus': {
				opacity: 1,

				transform: 'none !important',
			},
		},
		[`@media (hover: hover) and (pointer: fine)`]: {
			[`.${HeadingAnchorWrapperClassName}`]: {
				'> button': {
					opacity: 0,
					transform: `translate(-8px, 0px)`,
					transition: `opacity 0.2s ease 0s, transform 0.2s ease 0s`,
				},
			},

			'&:hover': {
				[`.${HeadingAnchorWrapperClassName} > button`]: {
					opacity: 1,

					transform: 'none !important',
				},
			},
		},
	},

	// Line-height for heading anchor based on heading level (via data-level attribute on .renderer-heading-wrapper)
	'& .renderer-heading-wrapper[data-level="1"]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${28 / 24}em`,
		},
	},
	'& .renderer-heading-wrapper[data-level="2"]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${24 / 20}em`,
		},
	},
	'& .renderer-heading-wrapper[data-level="3"]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${20 / 16}em`,
		},
	},
	'& .renderer-heading-wrapper[data-level="4"]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${16 / 14}em`,
		},
	},
	'& .renderer-heading-wrapper[data-level="5"]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${16 / 12}em`,
		},
	},
	'& .renderer-heading-wrapper[data-level="6"]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${16 / 11}em`,
		},
	},
});

const headingAnchorButtonFocusVisibleStyles = css({
	[`.${HeadingAnchorWrapperClassName}`]: {
		'button:focus-visible': {
			outline: `2px solid ${token('color.border.focused')}`,
			borderRadius: token('radius.small', '3px'),
		},
	},
});

const akEditorBreakpointForSmallDevice = '1266px';

const responsiveBreakoutWidth = css({
	'--ak-editor--breakout-container-without-gutter-width': `calc(100cqw - ${akEditorGutterPadding}px * 2)`,

	// Corresponds to the legacyContentStyles from `@atlaskit/editor-core` meant to introduce responsive breakout width.
	'--ak-editor--breakout-wide-layout-width': `${akEditorCalculatedWideLayoutWidthSmallViewport}px`,

	[`@media (min-width: ${akEditorBreakpointForSmallDevice})`]: {
		'--ak-editor--breakout-wide-layout-width': `${akEditorCalculatedWideLayoutWidth}px`,
	},
});

const responsiveBreakoutWidthFullWidth = css({
	'--ak-editor--breakout-container-without-gutter-width': '100cqw',

	// Corresponds to the legacyContentStyles from `@atlaskit/editor-core` meant to introduce responsive breakout width.
	'--ak-editor--breakout-wide-layout-width': `${akEditorCalculatedWideLayoutWidthSmallViewport}px`,

	[`@media (min-width: ${akEditorBreakpointForSmallDevice})`]: {
		'--ak-editor--breakout-wide-layout-width': `${akEditorCalculatedWideLayoutWidth}px`,
	},
});

const responsiveBreakoutWidthWithReducedPadding = css({
	'--ak-editor--breakout-container-without-gutter-width': `calc(100cqw - var(--ak-renderer--full-page-gutter) * 2)`,

	// Corresponds to the legacyContentStyles from `@atlaskit/editor-core` meant to introduce responsive breakout width.
	'--ak-editor--breakout-wide-layout-width': `${akEditorCalculatedWideLayoutWidthSmallViewport}px`,
	[`@media (min-width: ${akEditorBreakpointForSmallDevice})`]: {
		'--ak-editor--breakout-wide-layout-width': `${akEditorCalculatedWideLayoutWidth}px`,
	},

	[`@media (max-width: ${akEditorFullPageNarrowBreakout}px)`]: {
		'--ak-editor--breakout-container-without-gutter-width': `calc(100cqw - var(--ak-renderer--full-page-gutter) * 2)`,
	},
});

const hideHeadingCopyLinkWrapperStylesDuplicateAnchor = css({
	'& h1, & h2, & h3, & h4, & h5, & h6': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			'&:focus-within': {
				opacity: 1,
			},
		},
		[`@media (hover: hover) and (pointer: fine)`]: {
			[`.${HeadingAnchorWrapperClassName}`]: {
				opacity: 0,
				transition: `opacity 0.2s ease 0s`,
			},

			'&:hover': {
				[`.${HeadingAnchorWrapperClassName}`]: {
					opacity: 1,
				},
			},
		},
	},
});

const hideHeadingCopyLinkWrapperStyles = css({
	'& .renderer-heading-wrapper': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			'&:focus-within': {
				opacity: 1,
			},
		},
		[`@media (hover: hover) and (pointer: fine)`]: {
			[`.${HeadingAnchorWrapperClassName}`]: {
				opacity: 0,
				transition: `opacity 0.2s ease 0s`,
			},

			'&:hover': {
				[`.${HeadingAnchorWrapperClassName}`]: {
					opacity: 1,
				},
			},
		},
	},
});

const rendererFullPageStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	maxWidth: `${akEditorDefaultLayoutWidth}px`,
	margin: '0 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	padding: `0 ${FullPagePadding}px`,
});

const rendererFullPageStylesWithReducedPadding = css({
	'--ak-renderer--full-page-gutter': `${FullPagePadding}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	maxWidth: `${akEditorDefaultLayoutWidth}px`,
	margin: '0 auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	padding: `0 var(--ak-renderer--full-page-gutter)`,

	[`@media (max-width: ${akEditorFullPageNarrowBreakout}px)`]: {
		'--ak-renderer--full-page-gutter': `${akEditorGutterPaddingReduced}px`,
	},
});

const oldRendererFullWidthStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	maxWidth: `${akEditorFullWidthLayoutWidth}px`,
	margin: `0 auto`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.fabric-editor-breakout-mark:not([data-has-width="true"]), .ak-renderer-extension': {
		width: '100% !important',
	},
});

const rendererFullWidthStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	maxWidth: `${akEditorFullWidthLayoutWidth}px`,
	margin: `0 auto`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.fabric-editor-breakout-mark:not([data-has-width="true"])': {
		width: '100% !important',
	},
});

const oldRendererMaxWidthStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	maxWidth: `${akEditorMaxWidthLayoutWidth}px`,
	margin: `0 auto`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.fabric-editor-breakout-mark:not([data-has-width="true"]), .ak-renderer-extension': {
		width: '100% !important',
	},
});

const rendererMaxWidthStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
	maxWidth: `${akEditorMaxWidthLayoutWidth}px`,
	margin: `0 auto`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.fabric-editor-breakout-mark:not([data-has-width="true"])': {
		width: '100% !important',
	},
});

const rendererFullWidthStylesForTableResizing = css({
	'.pm-table-container': {
		width: '100% !important',
	},
});

const rovoTelepointerStyles = css({
	[`#${TELEPOINTER_ID}`]: {
		display: 'inline-block',
		position: 'relative',
		width: '1.5px',
		height: '24px',
		backgroundColor: token('color.background.brand.bold'),
		marginLeft: token('space.025'),

		'&::after': {
			content: '""',
			position: 'absolute',
			display: 'block',
			top: 0,
			left: 0,
			width: '31.5px',
			height: '15.5px',
			borderRadius: `0px ${token('space.050')} ${token('space.050')} 0px`,
			background:
				// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography, @atlaskit/design-system/ensure-design-token-usage
				'linear-gradient(to right, #1868db 0px, #1868db 1.5px, transparent 1.5px) 0 0 / 100% 100% no-repeat, conic-gradient(from 270deg at 50% 50%, #1868db 0deg, #1868db 115deg, #fca700 115deg, #fca700 180deg, #bf63f3 180deg, #bf63f3 310deg, #82b536 310deg, #82b536 359.96deg, #1868db 360deg)',
		},

		'&::before': {
			content: '"Rovo"',
			position: 'absolute',
			fontFamily: token('font.family.body'),
			fontWeight: token('font.weight.semibold'),
			color: token('color.text.inverse'),
			backgroundColor: token('color.text'),
			top: 1,
			left: 1,
			borderRadius: `0px ${token('space.050')} ${token('space.050')} 0px`,
			zIndex: 1,
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: '10px',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			padding: '0.5px 2.5px',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: 'initial',
		},
	},
});

const whitespaceSharedStyles = css({
	wordWrap: 'break-word',
	whiteSpace: 'pre-wrap',
});

const blockquoteSharedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& blockquote': {
		boxSizing: 'border-box',
		color: 'inherit',
		width: '100%',
		display: 'inline-block',
		paddingLeft: token('space.200'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		borderLeft: `${token('border.width.selected')} solid ${token('color.border')}`,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		margin: `${blockNodesVerticalMargin} 0 0 0`,
		marginRight: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		"[dir='rtl'] &": {
			paddingLeft: 0,
			paddingRight: token('space.200'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'&:first-child': {
			marginTop: 0,
		},
		'&::before': {
			content: "''",
		},
		'&::after': {
			content: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& p': {
			display: 'block',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'& table, & table:last-child': {
			display: 'inline-table',
		},
		// Workaround for overriding the inline-block display on last child of a blockquote set in CSS reset.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> .code-block:last-child, >.mediaSingleView-content-wrap:last-child, >.mediaGroupView-content-wrap:last-child':
			{
				display: 'block',
			},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'> .extensionView-content-wrap:last-child': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			display: 'block',
		},
	},
});

const headingsSharedStyles = css({
	'& h1': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-heading-h1)`,
		marginBottom: 0,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.45833em',
		'& strong': {
			// set all heading bold style to token font.weight.bold, as the refreshed typography tokens use font weight 700
			fontWeight: token('font.weight.bold'),
		},
		'&::before': {},
	},
	'& h2': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-heading-h2)`,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.4em',
		marginBottom: 0,
		'& strong': {
			// set all heading bold style to token font.weight.bold, as the refreshed typography tokens use font weight 700
			fontWeight: token('font.weight.bold'),
		},
	},
	'& h3': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-heading-h3)`,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.31249em',
		marginBottom: 0,
		'& strong': {
			// set all heading bold style to token font.weight.bold, as the refreshed typography tokens use font weight 700
			fontWeight: token('font.weight.bold'),
		},
	},
	'& h4': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-heading-h4)`,
		marginTop: token('space.250'),
		'& strong': {
			// set all heading bold style to token font.weight.bold, as the refreshed typography tokens use font weight 700
			fontWeight: token('font.weight.bold'),
		},
	},
	'& h5': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-heading-h5)`,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.45833em',
		textTransform: 'none',
		'& strong': {
			// set all heading bold style to token font.weight.bold, as the refreshed typography tokens use font weight 700
			fontWeight: token('font.weight.bold'),
		},
	},
	'& h6': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-heading-h6)`,
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.59091em',
		textTransform: 'none',
		'& strong': {
			// set all heading bold style to token font.weight.bold, as the refreshed typography tokens use font weight 700
			fontWeight: token('font.weight.bold'),
		},
	},
});

/**
 * When the copy-link a11y fix is enabled, the heading copy-link button is a sibling of the heading
 * text within `.renderer-heading-wrapper`. To ensure the button sits immediately after the last
 * rendered character (including when the heading wraps), the heading itself needs to participate in
 * inline layout.
 *
 * Since inline elements don't support vertical margins in normal flow, we migrate the UGC heading
 * `marginTop` values (from `headingsSharedStyles`) onto the wrapper.
 */
const headingWrapperInlineFlowStyles = css({
	// Ensure heading and copy-link button participate in inline flow so the button sits after the final wrapped line of text.
	'& .renderer-heading-wrapper > h1, & .renderer-heading-wrapper > h2, & .renderer-heading-wrapper > h3, & .renderer-heading-wrapper > h4, & .renderer-heading-wrapper > h5, & .renderer-heading-wrapper > h6':
		{
			display: 'inline',
		},

	[`& .renderer-heading-wrapper > .${HeadingAnchorWrapperClassName}`]: {
		display: 'inline',
		verticalAlign: 'baseline',
	},

	'& .renderer-heading-wrapper[data-level="1"]': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.45833em',
	},
	'& .renderer-heading-wrapper[data-level="2"]': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.4em',
	},
	'& .renderer-heading-wrapper[data-level="3"]': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.31249em',
	},
	'& .renderer-heading-wrapper[data-level="4"]': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.25em',
	},
	'& .renderer-heading-wrapper[data-level="5"]': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.45833em',
	},
	'& .renderer-heading-wrapper[data-level="6"]': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginTop: '1.59091em',
	},
});

const headingWithAlignmentStylesDuplicateAnchor = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.fabric-editor-block-mark.fabric-editor-alignment:not(:first-child)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> h1:first-child': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '1.667em',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		' > h2:first-child': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '1.8em',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> h3:first-child': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '2em',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> h4:first-child': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '1.357em',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> h5:first-child': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '1.667em',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> h6:first-child': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '1.455em',
		},
	},
	// Set marginTop: 0 if alignment block is next to a gap cursor or widget that is first child
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.ProseMirror-gapcursor:first-child + .fabric-editor-block-mark.fabric-editor-alignment, .ProseMirror-widget:first-child + .fabric-editor-block-mark.fabric-editor-alignment, .ProseMirror-widget:first-child + .ProseMirror-widget:nth-child(2) + .fabric-editor-block-mark.fabric-editor-alignment':
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'> :is(h1, h2, h3, h4, h5, h6):first-child': {
				marginTop: '0',
			},
		},
});

const headingWithAlignmentStyles = css({
	// Center alignment for heading wrapper - flex container needs justify-content instead of text-align
	'.fabric-editor-block-mark[data-align="center"] > .renderer-heading-wrapper': {
		justifyContent: 'center',
	},
	// Right/end alignment for heading wrapper - flex container needs justify-content instead of text-align
	'.fabric-editor-block-mark[data-align="end"] > .renderer-heading-wrapper': {
		justifyContent: 'flex-end',
	},

	// Set marginTop: 0 if alignment block is next to a gap cursor or widget that is first child
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.ProseMirror-gapcursor:first-child + .fabric-editor-block-mark.fabric-editor-alignment, .ProseMirror-widget:first-child + .fabric-editor-block-mark.fabric-editor-alignment, .ProseMirror-widget:first-child + .ProseMirror-widget:nth-child(2) + .fabric-editor-block-mark.fabric-editor-alignment':
		{
			// With inline headings, apply marginTop to the wrapper (not the h1-h6).
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'> .renderer-heading-wrapper:first-child': {
				marginTop: 0,
			},
		},
});

const ruleSharedStyles = css({
	'& hr': {
		border: 'none',
		backgroundColor: token('color.border'),
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage/preview, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		margin: `${akEditorLineHeight}em 0`,
		height: '2px',
		borderRadius: token('radius.full'),
	},
});

// When cleaning up `platform_editor_content_mode_button_mvp` simplify the name/ use the other paragraph style name
const paragraphSharedStylesWithEditorUGC = css({
	'& p': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-normal-text)`,
		marginTop: blockNodesVerticalMargin,
		marginBottom: 0,
	},
});

const paragraphStylesUGCScaledMargin = css({
	'& p': {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		font: `var(--ak-renderer-editor-font-normal-text)`,
		marginTop: scaledBlockNodesVerticalMargin,
		marginBottom: 0,
	},
});

const listsSharedStyles = css({
	/* =============== INDENTATION SPACING ========= */
	'ul, ol': {
		boxSizing: 'border-box',
		paddingLeft: `var(--ed--list--item-counter--padding, ${listItemCounterPadding}px)`,
	},

	[`${orderedListSelector}, ${bulletListSelector}`]: {
		/*
		Ensures list item content adheres to the list's margin instead
		of filling the entire block row. This is important to allow
		clicking interactive elements which are floated next to a list.

		For some history and context on this block, see PRs related to tickets.:
		@see ED-6551 - original issue.
		@see ED-7015 - follow up issue.
		@see ED-7447 - flow-root change.

		used to have display: 'table' in tag template style but not supported in object styles
		removed display: 'table' as 'flow-root' is supported in latest browsers

		@see https://css-tricks.com/display-flow-root/
	*/
		display: 'flow-root',
	},

	/* =============== INDENTATION AESTHETICS ========= */
	/**
		We support nested lists up to six levels deep.
	**/
	/* LEGACY LISTS */
	'ul, ul ul ul ul': {
		listStyleType: 'disc',
	},

	'ul ul, ul ul ul ul ul': {
		listStyleType: 'circle',
	},

	'ul ul ul, ul ul ul ul ul ul': {
		listStyleType: 'square',
	},

	'ol, ol ol ol ol': {
		listStyleType: 'decimal',
	},
	'ol ol, ol ol ol ol ol': {
		listStyleType: 'lower-alpha',
	},
	'ol ol ol, ol ol ol ol ol ol': {
		listStyleType: 'lower-roman',
	},

	/* PREDICTABLE LISTS */
	'ol[data-indent-level="1"], ol[data-indent-level="4"]': {
		listStyleType: 'decimal',
	},

	'ol[data-indent-level="2"], ol[data-indent-level="5"]': {
		listStyleType: 'lower-alpha',
	},

	'ol[data-indent-level="3"], ol[data-indent-level="6"]': {
		listStyleType: 'lower-roman',
	},

	'ul[data-indent-level="1"], ul[data-indent-level="4"]': {
		listStyleType: 'disc',
	},

	'ul[data-indent-level="2"], ul[data-indent-level="5"]': {
		listStyleType: 'circle',
	},

	'ul[data-indent-level="3"], ul[data-indent-level="6"]': {
		listStyleType: 'square',
	},
});

/*
	Firefox does not handle empty block element inside li tag.
	If there is not block element inside li tag,
	then firefox sets inherited height to li
	However, if there is any block element and if it's empty
	(or has empty inline element) then
	firefox sets li tag height to zero.
	More details at
	https://product-fabric.atlassian.net/wiki/spaces/~455502413/pages/3149365890/ED-14110+Investigation
*/
const listsSharedStylesForGekko = css({
	'ul, ol': {
		'li p:empty, li p > span:empty': {
			display: 'inline-block',
		},
	},
});

/**
 * Hides list markers for "wrapper items" - list items that only contain nested lists with no other content.
 * These wrapper items have no meaningful content themselves, only nested lists below.
 * Applied when platform_editor_flexible_list_schema experiment is enabled.
 */
const listItemHiddenMarkerStyles = css({
	// Hide markers and remove spacing for wrapper list items (items containing only nested lists)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'li:has(> ul:only-child), li:has(> ol:only-child)': {
		listStyleType: 'none',
		marginTop: 0,
		marginBottom: 0,
	},
	// Remove margin from nested lists inside wrapper list items to avoid double spacing
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'li:has(> ul:only-child) > ul, li:has(> ol:only-child) > ol': {
		marginTop: 0,
	},
	// Remove top margin from nested taskLists not preceded by a sibling taskItem
	'div[data-task-list-local-id] > div[data-task-list-local-id]': {
		marginTop: 0,
	},
	// Restore margin when a nested taskList follows a taskItem
	'div[data-task-local-id] + div[data-task-list-local-id]': {
		marginTop: token('space.050'),
	},
});

const indentationSharedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-indentation-mark': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-level='1']": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '30px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-level='2']": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '60px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-level='3']": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '90px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-level='4']": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '120px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-level='5']": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '150px',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"&[data-level='6']": {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '180px',
		},
	},
});
const indentationSharedStylesWithMarginFix = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.fabric-editor-indentation-mark': {
		// Prevent marginTop of p:first-child overrode by batch.css
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'p:first-child': {
			marginTop: blockNodesVerticalMargin,
		},
	},
});

const blockMarksSharedStylesDuplicateAnchor = css({
	/**
	 * We need to remove margin-top from first item
	 * inside doc, tableCell, tableHeader, blockquote, etc.
	 */
	[`*:not(.fabric-editor-block-mark) >,
  	*:not(.fabric-editor-block-mark) >
    div.fabric-editor-block-mark:first-of-type
	:not(.fabric-editor-indentation-mark)
	:not(.fabric-editor-alignment),
  	.fabric-editor-alignment:first-of-type:first-child,
  	.fabric-editor-font-size:first-of-type:first-child,
  	.ProseMirror .fabric-editor-indentation-mark:first-of-type:first-child`]: {
		'p, h1, h2, h3, h4, h5, h6, .heading-wrapper': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			':first-child:not(style), style:first-child + *': {
				marginTop: 0,
			},
		},
	},
});

const blockMarksSharedStyles = css({
	/**
	 * We need to remove margin-top from first item
	 * inside doc, tableCell, tableHeader, blockquote, etc.
	 */
	[`*:not(.fabric-editor-block-mark) >,
  	*:not(.fabric-editor-block-mark) >
    div.fabric-editor-block-mark:first-of-type
	:not(.fabric-editor-indentation-mark)
	:not(.fabric-editor-alignment),
  	.fabric-editor-alignment:first-of-type:first-child,
  	.fabric-editor-font-size:first-of-type:first-child,
  	.ProseMirror .fabric-editor-indentation-mark:first-of-type:first-child`]: {
		'p, .heading-wrapper': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			':first-child:not(style), style:first-child + *': {
				marginTop: 0,
			},
		},
		'.renderer-heading-wrapper': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			':first-child:not(style), style:first-child + *': {
				'h1, h2, h3, h4, h5, h6': {
					marginTop: 0,
				},
			},
		},
	},
});

const codeMarkSharedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.code': {
		'--ds--code--bg-color': token('color.background.neutral'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '0.875em',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontFamily: token('font.family.code'),
		fontWeight: token('font.weight.regular'),
		backgroundColor: `var(--ds--code--bg-color,${token('color.background.neutral')})`,
		color: token('color.text'),
		borderStyle: 'none',
		borderRadius: token('radius.small', '3px'),
		display: 'inline',
		padding: '2px 0.5ch',
		boxDecorationBreak: 'clone',
		overflow: 'auto',
		overflowWrap: 'break-word',
		whiteSpace: 'pre-wrap',
	},
});

const extensionStyle = css({
	// Sets fontSize of extensions to match base font size which scales with the renderer contentMode prop
	// exceptions:
	// - nested renderers - bodied extensions have nested renderers adopt the contentMode prop themselves so should not be touched
	// - legacy status lozenge - some extensions use the .aui-lozenge class for a legacy status lozenge and is not designed to scale
	// - .ak-renderer-extension .ak-renderer-document .ak-renderer-extension:not(:has(.ak-renderer-document)) :not(.aui-lozenge, .status-lozenge-span *)
	//	 extensions inside extension documents (like ones with their own renderer) will need the font size to be applied again (e.g. TOC inside Excerpt, or TOC inside Page Properties inside LCM inside table)
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.ak-renderer-extension :not(.ak-renderer-extension .ak-renderer-document *, .aui-lozenge, .status-lozenge-span *), .ak-renderer-extension .ak-renderer-document .ak-renderer-extension:not(:has(.ak-renderer-document)) :not(.aui-lozenge, .status-lozenge-span *)':
		{
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: 'var(--ak-renderer-base-font-size)',
		},
});

const oldExtensionAsInlineStyle = css({
	[`.${RendererCssClassName.DOCUMENT} [data-as-inline="on"]`]: {
		display: 'inline-block',
	},
	[`.${RendererCssClassName.DOCUMENT} .${RendererCssClassName.EXTENSION_AS_INLINE}`]: {
		display: 'inline-block',
		// use !important here because the current width has !important applied to it and it's not working when used in React style prop
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		width: 'auto !important',
		marginTop: 0,
	},
	[`.${RendererCssClassName.EXTENSION_AS_INLINE} .${RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER}`]:
		{
			display: 'inline-block',
			overflowX: 'visible',
			containerType: 'normal',
		},
	[`.${RendererCssClassName.EXTENSION_AS_INLINE} .${RendererCssClassName.EXTENSION_INNER_WRAPPER}`]:
		{
			display: 'inline-block',
		},
});

const extensionAsInlineStyle = css({
	[`.${RendererCssClassName.DOCUMENT} [data-as-inline="on"]`]: {
		display: 'inline-block',
	},
	[`.${RendererCssClassName.DOCUMENT} .${RendererCssClassName.EXTENSION_AS_INLINE}`]: {
		display: 'inline-block',
		width: 'auto',
		marginTop: 0,
	},
	[`.${RendererCssClassName.EXTENSION_AS_INLINE} .${RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER}`]:
		{
			display: 'inline-block',
			overflowX: 'visible',
			containerType: 'normal',
		},
	[`.${RendererCssClassName.EXTENSION_AS_INLINE} .${RendererCssClassName.EXTENSION_INNER_WRAPPER}`]:
		{
			display: 'inline-block',
		},
});

// Removes the blockNodesVerticalMargin styling for inline extensions, i.e. borderless excerpt-include
const inlineExtensionRendererMarginFix = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.ak-renderer-document .inline-extension-renderer .ak-renderer-extension': {
		marginTop: 0,
	},
});

const shadowSharedStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`& .${shadowClassNames.RIGHT_SHADOW}::before, .${shadowClassNames.RIGHT_SHADOW}::after, .${shadowClassNames.LEFT_SHADOW}::before, .${shadowClassNames.LEFT_SHADOW}::after`]:
		{
			display: 'none',
			position: 'absolute',
			pointerEvents: 'none',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			zIndex: akEditorShadowZIndex,
			width: '8px',
			content: "''",
			height: 'calc(100%)',
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`& .${shadowClassNames.RIGHT_SHADOW}, .${shadowClassNames.LEFT_SHADOW}`]: {
		position: 'relative',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`& .${shadowClassNames.LEFT_SHADOW}::before`]: {
		background: `linear-gradient( to left, transparent 0, ${token(
			'elevation.shadow.overflow.spread',
		)} 140% ), linear-gradient( to right, ${token(
			'elevation.shadow.overflow.perimeter',
		)} 0px, transparent 1px )`,
		top: '0px',
		left: 0,
		display: 'block',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`& .${shadowClassNames.RIGHT_SHADOW}::after`]: {
		background: `linear-gradient( to right, transparent 0, ${token(
			'elevation.shadow.overflow.spread',
		)} 140% ), linear-gradient( to left, ${token(
			'elevation.shadow.overflow.perimeter',
		)} 0px, transparent 1px )`,
		right: '0px',
		top: '0px',
		display: 'block',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`& .${shadowObserverClassNames.SENTINEL_LEFT}`]: {
		height: '100%',
		width: '0px',
		minWidth: '0px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`& .${shadowObserverClassNames.SENTINEL_RIGHT}`]: {
		height: '100%',
		width: '0px',
		minWidth: '0px',
	},
});

const dateSharedStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`.${DateSharedCssClassName.DATE_WRAPPER} span`]: {
		whiteSpace: 'unset',
	},
});

const textColorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.fabric-text-color-mark': {
		color: 'var(--custom-palette-color, inherit)',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a .fabric-text-color-mark': {
		color: 'unset',
	},
});

const backgroundColorStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.fabric-background-color-mark': {
		backgroundColor: 'var(--custom-palette-color, inherit)',
		borderRadius: token('radius.xsmall'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingTop: '1px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingBottom: '2px',
		boxDecorationBreak: 'clone',
	},
	// Don't show text highlight styling when there is an inline comment
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.fabric-background-color-mark .ak-editor-annotation': {
		backgroundColor: 'unset',
	},
});

// Don't show text highlight styling when there is a hyperlink.
// Conditionally applied when the highlight-on-links experiment is off.
const highlightLinksUnsetStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'a .fabric-background-color-mark': {
		backgroundColor: 'unset',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const textHighlightPaddingStyles: SerializedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.fabric-background-color-mark:has(.background-color-padding-left)': {
		paddingLeft: token('space.025'),
		marginLeft: token('space.negative.025'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'.fabric-background-color-mark:has(.background-color-padding-right)': {
		paddingRight: token('space.025'),
		marginRight: token('space.negative.025'),
	},
});

const tasksAndDecisionsStyles = css({
	'.ProseMirror': {
		[`.taskItemView-content-wrap,
		.${TaskDecisionSharedCssClassName.DECISION_CONTAINER}`]: {
			position: 'relative',
			minWidth: `${akEditorTableCellMinWidth}px`,
		},

		[`.${TaskDecisionSharedCssClassName.DECISION_CONTAINER}`]: {
			marginTop: 0,
		},

		[`.${TaskDecisionSharedCssClassName.TASK_CONTAINER}`]: {
			'span[contenteditable="false"]': {
				height: `${akEditorLineHeight}em`,
			},
		},

		[`.${TaskDecisionSharedCssClassName.TASK_ITEM}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: akEditorLineHeight,
		},
	},

	'div[data-task-local-id]': {
		'span[contenteditable="false"]': {
			height: `${akEditorLineHeight}em`,
		},
		'span[contenteditable="false"] + div': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: `${akEditorLineHeight}em`,
		},
	},

	'div[data-task-list-local-id]': {
		marginTop: token('space.150'),
		marginRight: 0,
		marginBottom: 0,
		marginLeft: 0,
		// If task item is not first in the list then set margin top to 4px.
		'div + div': {
			marginTop: token('space.050'),
		},
	},

	// If task list is not first in the document then set margin top to 4px.
	'div[data-task-list-local-id] div[data-task-list-local-id]': {
		marginTop: token('space.050'),
		marginLeft: token('space.300'),
	},

	/* When action list is inside panel */
	'.ak-editor-panel__content': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> div[data-task-list-local-id]:first-child': {
			margin: '0 0 0 0 !important',
		},
	},
});

const headingPanelStyles = css({
	// Removes the margin top from the first heading in a panel
	'.ak-editor-panel__content': {
		'> .renderer-heading-wrapper:first-child': {
			marginTop: 0,
		},
	},
});

const smartCardStyles = css({
	[`.${SmartCardSharedCssClassName.BLOCK_CARD_CONTAINER}`]: {
		display: 'block',
		margin: `${blockNodesVerticalMargin} 0 0`,
		maxWidth: `${8 * 95}px`,
	},
});

const smartCardStylesAvatarFix = css({
	[`.${SmartCardSharedCssClassName.BLOCK_CARD_CONTAINER}:not(.${SmartCardSharedCssClassName.DATASOURCE_CONTAINER})`]:
		{
			/* EDM-11991: Fix list plugin adding padding to ADS AvatarGroup start */
			'ul, ol': {
				paddingLeft: 'inherit',
			},
			/* EDM-11991: Fix list plugin add padding to ADS AvatarGroup end */
		},
});

const smartCardStylesAvatarMarginFix = css({
	[`.${SmartCardSharedCssClassName.BLOCK_CARD_CONTAINER}:not(.${SmartCardSharedCssClassName.DATASOURCE_CONTAINER})`]:
		{
			'ul, ol': {
				marginRight: 'inherit',
			},
		},
});

const smartCardStylesAvatarListZeroMarginTop = css({
	[`.${SmartCardSharedCssClassName.BLOCK_CARD_CONTAINER}:not(.${SmartCardSharedCssClassName.DATASOURCE_CONTAINER})`]:
		{
			'ul, ol': {
				marginTop: 0,
			},
		},
});

// In Renderer in Jira there is no SmartCardSharedCssClassName.INLINE_CARD_CONTAINER, so using data-inline-cart="true"
const headerSmartCardStyles = css({
	'[data-inline-card="true"]': {
		'[data-inlinecard-button-overlay="icon-wrapper-line-height"] span': {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: 0,
		},
	},
});

// Flex wrapper for centering without transform (platform_editor_flex_based_centering).
// flex: 1 1 0% + minWidth: 0 so the wrapper takes full width when it's a flex item (full-width nodes otherwise render narrow).
// flexShrink: 0 on child so the node keeps size.
const centerWrapperStyles = css({
	[`.${RendererCssClassName.FLEX_CENTER_WRAPPER}`]: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'flex-start',
		width: '100%',
		flex: '1 1 0%',
		minWidth: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > *': {
			flexShrink: 0,
		},
	},
});

const baseOtherStylesDuplicateAnchor = css({
	'& .UnknownBlock': {
		fontFamily: token('font.family.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: `${14 / 16}rem`,
		fontWeight: token('font.weight.regular'),
		whiteSpace: 'pre-wrap',
		wordWrap: 'break-word',
	},
	'& span.date-node': {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small', '3px'),
		color: token('color.text'),
		paddingTop: token('space.025'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.050'),
		margin: `0 1px`,
		transition: `background 0.3s`,
	},
	'& span.date-node-highlighted': {
		backgroundColor: token('color.background.danger'),
		color: token('color.text.danger'),
	},
	'& .renderer-image': {
		maxWidth: '100%',
		display: 'block',
		marginTop: token('space.300'),
		marginRight: 0,
		marginBottom: token('space.300'),
		marginLeft: 0,
	},

	[`.${richMediaClassName}.rich-media-wrapped + .${richMediaClassName}:not(.rich-media-wrapped)`]: {
		clear: 'both',
	},

	[`& .code-block,
	& blockquote,
	& hr,
	& > div > div:not(.rich-media-wrapped),
	.${richMediaClassName}.rich-media-wrapped + .rich-media-wrapped + *:not(.rich-media-wrapped),
	.${richMediaClassName}.rich-media-wrapped + div:not(.rich-media-wrapped),
	.${richMediaClassName}.image-align-start,
		.${richMediaClassName}.image-center,
		.${richMediaClassName}.image-align-end`]: {
		clear: 'both',
	},

	'& .rich-media-wrapped': {
		'& + .renderer-heading-wrapper': {
			'h1, h2, h3, h4, h5, h6': {
				marginTop: token('space.100'),
			},
		},
	},

	// originally copied the block after mediaSharedStyle
	'div[class^="image-wrap-"] + div[class^="image-wrap-"]': {
		marginLeft: 0,
		marginRight: 0,
	},

	/* Breakout for tables and extensions */
	[`.${RendererCssClassName.DOCUMENT} >`]: {
		[`*:not([data-mark-type='fragment']) .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
			// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
			// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
			// TODO - improve inline style logic on table container so important styles aren't required here
			width: '100% !important',
			left: '0 !important',
		},
		[`[data-mark-type='fragment'] * .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
			// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
			// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
			// TODO - improve inline style logic on table container so important styles aren't required here
			width: '100% !important',
			left: '0 !important',
		},
		[`* .${RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER}`]: {
			overflowX: 'auto',
		},
		[`& .${RendererCssClassName.EXTENSION}:first-child`]: {
			marginTop: 0,
		},
	},

	[`.${RendererCssClassName.DOCUMENT}`]: {
		[`.${RendererCssClassName.EXTENSION}`]: {
			marginTop: `${blockNodesVerticalMargin}`,
		},
		[`.${RendererCssClassName.STICKY_SAFE_CENTER_WRAPPER} .${RendererCssClassName.EXTENSION}`]: {
			marginBottom: 0,
		},
		/* platform_editor_flex_based_centering: embed card center wrapper has margin; zero MediaSingle vertical margin when wrapped so it doesn't double */
		[`.${RendererCssClassName.EMBED_CARD_CENTER_WRAPPER} .mediaSingleView-content-wrap`]: {
			marginTop: 0,
			marginBottom: 0,
		},
		[`.${RendererCssClassName.STICKY_SAFE_BREAKOUT_INNER} > *:first-child`]: {
			marginTop: 0,
		},
		[`.${RendererCssClassName.STICKY_SAFE_BREAKOUT_INNER} > *:last-child`]: {
			marginBottom: 0,
		},
		[`.${RendererCssClassName.EMBED_CARD_CENTER_WRAPPER} > .mediaSingleView-content-wrap > *:first-child`]:
			{
				marginTop: 0,
			},
		[`.${RendererCssClassName.EMBED_CARD_CENTER_WRAPPER} > .mediaSingleView-content-wrap > *:last-child`]:
			{
				marginBottom: 0,
			},
		[`.${RendererCssClassName.BLOCK_CARD_DATASOURCE_CENTER_WRAPPER} > div > *:first-child`]: {
			marginTop: 0,
		},
		[`.${RendererCssClassName.BLOCK_CARD_DATASOURCE_CENTER_WRAPPER} > div > *:last-child`]: {
			marginBottom: 0,
		},
		[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER}`]: {
			overflowX: 'auto',
		},

		[`.${shadowObserverClassNames.SHADOW_CONTAINER} .${TableSharedCssClassName.TABLE_NODE_WRAPPER}`]:
			{
				display: 'flex',
			},
	},
});

const hideExtensionStyles = css({
	[`.${RendererCssClassName.EXTENSION}:has([data-extension-key='hide'])`]: {
		marginTop: 0,
	},
});

const extensionCenterAlignLegacyStyles = css({
	[`.${RendererCssClassName.DOCUMENT}`]: {
		[`.${RendererCssClassName.EXTENSION_CENTER_ALIGN}`]: {
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginLeft: '50%',
			transform: 'translateX(-50%)',
		},
	},
});

const baseOtherStyles = css({
	'& .UnknownBlock': {
		fontFamily: token('font.family.body'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: `${14 / 16}rem`,
		fontWeight: token('font.weight.regular'),
		whiteSpace: 'pre-wrap',
		wordWrap: 'break-word',
	},
	'& span.date-node': {
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.small', '3px'),
		color: token('color.text'),
		paddingTop: token('space.025'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.050'),
		margin: `0 1px`,
		transition: `background 0.3s`,
	},
	'& span.date-node-highlighted': {
		backgroundColor: token('color.background.danger'),
		color: token('color.text.danger'),
	},
	'& .renderer-image': {
		maxWidth: '100%',
		display: 'block',
		marginTop: token('space.300'),
		marginRight: 0,
		marginBottom: token('space.300'),
		marginLeft: 0,
	},

	[`.${richMediaClassName}.rich-media-wrapped + .${richMediaClassName}:not(.rich-media-wrapped)`]: {
		clear: 'both',
	},

	[`& .code-block,
	& blockquote,
	& hr,
	& > div > div:not(.rich-media-wrapped),
	.${richMediaClassName}.rich-media-wrapped + .rich-media-wrapped + *:not(.rich-media-wrapped),
	.${richMediaClassName}.rich-media-wrapped + div:not(.rich-media-wrapped),
	.${richMediaClassName}.image-align-start,
		.${richMediaClassName}.image-center,
		.${richMediaClassName}.image-align-end`]: {
		clear: 'both',
	},

	'& .rich-media-wrapped': {
		'& + .renderer-heading-wrapper': {
			'h1, h2, h3, h4, h5, h6': {
				marginTop: token('space.100'),
			},
		},
	},

	// originally copied the block after mediaSharedStyle
	'div[class^="image-wrap-"] + div[class^="image-wrap-"]': {
		marginLeft: 0,
		marginRight: 0,
	},

	/* Breakout for tables and extensions */
	[`.${RendererCssClassName.DOCUMENT} >`]: {
		[`*:not([data-mark-type='fragment']) .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
			// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
			// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
			// TODO - improve inline style logic on table container so important styles aren't required here
			width: '100% !important',
			left: '0 !important',
		},
		[`[data-mark-type='fragment'] * .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
			// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
			// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
			// TODO - improve inline style logic on table container so important styles aren't required here
			width: '100% !important',
			left: '0 !important',
		},
		[`* .${RendererCssClassName.EXTENSION_OVERFLOW_CONTAINER}`]: {
			overflowX: 'auto',
		},
		[`& .${RendererCssClassName.EXTENSION}:first-child`]: {
			marginTop: 0,
		},
	},

	[`.${RendererCssClassName.DOCUMENT}`]: {
		[`.${RendererCssClassName.EXTENSION}`]: {
			marginTop: `${blockNodesVerticalMargin}`,
		},
		[`.${RendererCssClassName.STICKY_SAFE_CENTER_WRAPPER} .${RendererCssClassName.EXTENSION}`]: {
			marginBottom: 0,
		},
		/* platform_editor_flex_based_centering: embed card center wrapper has margin; zero MediaSingle vertical margin when wrapped so it doesn't double */
		[`.${RendererCssClassName.EMBED_CARD_CENTER_WRAPPER} .mediaSingleView-content-wrap`]: {
			marginTop: 0,
			marginBottom: 0,
		},
		[`.${RendererCssClassName.STICKY_SAFE_BREAKOUT_INNER} > *:first-child`]: {
			marginTop: 0,
		},
		[`.${RendererCssClassName.STICKY_SAFE_BREAKOUT_INNER} > *:last-child`]: {
			marginBottom: 0,
		},
		[`.${RendererCssClassName.EMBED_CARD_CENTER_WRAPPER} > .mediaSingleView-content-wrap > *:first-child`]:
			{
				marginTop: 0,
			},
		[`.${RendererCssClassName.EMBED_CARD_CENTER_WRAPPER} > .mediaSingleView-content-wrap > *:last-child`]:
			{
				marginBottom: 0,
			},
		[`.${RendererCssClassName.BLOCK_CARD_DATASOURCE_CENTER_WRAPPER} > div > *:first-child`]: {
			marginTop: 0,
		},
		[`.${RendererCssClassName.BLOCK_CARD_DATASOURCE_CENTER_WRAPPER} > div > *:last-child`]: {
			marginBottom: 0,
		},
		[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER}`]: {
			overflowX: 'auto',
		},

		[`.${shadowObserverClassNames.SHADOW_CONTAINER} .${TableSharedCssClassName.TABLE_NODE_WRAPPER}`]:
			{
				display: 'flex',
			},
	},
});

const alignedHeadingAnchorStyleDuplicateAnchor = css({
	// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
	// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
	// TODO Delete this comment after verifying space token -> previous value `margin: 6px`
	'.fabric-editor-block-mark[data-align] >': {
		'h1, h2, h3, h4, h5, h6': {
			position: 'relative',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.fabric-editor-block-mark:not([data-align="center"])[data-align]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			marginTop: 0,
			marginRight: token('space.075'),
			marginBottom: 0,
			marginLeft: 0,
			// If the anchor is right aligned then the left side of the heading
			// is aligned with the left side of the anchor.
			// In order to align as expected we transform it the width of the element (plus our expected 6px)
			// to the left
			transform: `translateX(calc(-100% - ${token('space.075')}))`,
		},
		'@media (hover: hover) and (pointer: fine)': {
			[`.${HeadingAnchorWrapperClassName} > button`]: {
				transform: `translate(8px, 0px)`,
			},
		},
	},
});

const alignedHeadingAnchorStyle = css({
	// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
	// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
	// TODO Delete this comment after verifying space token -> previous value `margin: 6px`
	'.fabric-editor-block-mark[data-align] >': {
		'.renderer-heading-wrapper': {
			'h1, h2, h3, h4, h5, h6': {
				position: 'relative',
			},
		},
	},
	// Center alignment for heading wrapper - flex container needs justify-content instead of text-align
	'.fabric-editor-block-mark[data-align="center"] > .renderer-heading-wrapper': {
		justifyContent: 'center',
	},
	// Right/end alignment for heading wrapper - flex container needs justify-content instead of text-align
	'.fabric-editor-block-mark[data-align="end"] > .renderer-heading-wrapper': {
		justifyContent: 'flex-end',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.fabric-editor-block-mark:not([data-align="center"])[data-align]': {
		[`.${HeadingAnchorWrapperClassName}`]: {
			marginTop: 0,
			marginRight: token('space.075'),
			marginBottom: 0,
			marginLeft: 0,
			// If the anchor is right aligned then the left side of the heading
			// is aligned with the left side of the anchor.
			// In order to align as expected we transform it the width of the element (plus our expected 6px)
			// to the left
			transform: `translateX(calc(-100% - ${token('space.075')}))`,
		},
		'@media (hover: hover) and (pointer: fine)': {
			[`.${HeadingAnchorWrapperClassName} > button`]: {
				transform: `translate(8px, 0px)`,
			},
		},
	},
});

const firstWrappedMediaStyles = css({
	// Remove gap between first wrapped mediaSingle and its fellow wrapped mediaSingle
	[`.${richMediaClassName}[class*="image-wrap-"]:has(+ .${richMediaClassName}[class*="image-wrap-"])`]:
		{
			marginRight: 0,
			marginLeft: 0,
		},
});
// Temporarily ignoring the below the owning team can add the ticket number for the TODO.  Context: https://atlassian.slack.com/archives/CPUEVD9MY/p1741565387326829
// eslint-disable-next-line @atlaskit/editor/enforce-todo-comment-format
// TODO: emotion refactor - there's a mediaSingleSharedNewStyle, but not originally used in the renderer.
const mediaSingleSharedStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`li .${richMediaClassName}`]: {
		margin: 0,
	},
	// Hack for chrome to fix media single position inside a list when media is the first child
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&.ua-chrome li > .mediaSingleView-content-wrap::before': {
		content: "''",
		display: 'block',
		height: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&.ua-firefox': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.mediaSingleView-content-wrap': {
			userSelect: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.captionView-content-wrap': {
			userSelect: 'text',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	".mediaSingleView-content-wrap[layout='center']": {
		clear: 'both',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`table .${richMediaClassName}`]: {
		marginTop: token('space.150'),
		marginBottom: token('space.150'),
		clear: 'both',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'&.image-wrap-left[data-layout], &.image-wrap-right[data-layout]': {
			clear: 'none',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:first-child': {
				marginTop: token('space.150'),
			},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`.${richMediaClassName}.image-wrap-right + .${richMediaClassName}.image-wrap-left`]: {
		clear: 'both',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`.${richMediaClassName}.image-wrap-left + .${richMediaClassName}.image-wrap-right, .${richMediaClassName}.image-wrap-right + .${richMediaClassName}.image-wrap-left, .${richMediaClassName}.image-wrap-left + .${richMediaClassName}.image-wrap-left, .${richMediaClassName}.image-wrap-right + .${richMediaClassName}.image-wrap-right`]:
		{
			marginRight: 0,
			marginLeft: 0,
		},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	[`@media all and (max-width: ${wrappedMediaBreakoutPoint}px)`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		"div.mediaSingleView-content-wrap[layout='wrap-left'], div.mediaSingleView-content-wrap[data-layout='wrap-left'], div.mediaSingleView-content-wrap[layout='wrap-right'], div.mediaSingleView-content-wrap[data-layout='wrap-right']":
			{
				float: 'none',
				overflow: 'auto',
				marginTop: token('space.150'),
				marginRight: 0,
				marginBottom: token('space.150'),
				marginLeft: 0,
			},
	},
});

// was shared between editor-core and renderer
const tableSharedStyle = css({
	// originally from packages/editor/editor-common/src/styles/shared/tableCell.ts
	// Hardcoding the background color for the table cells to avoid the use of inline styles
	'td[colorname="white" i], th[colorname="white" i]': {
		backgroundColor: `${token('elevation.surface')} !important`,
	},

	'td[colorname="light blue" i], th[colorname="light blue" i]': {
		backgroundColor: `${token('color.background.accent.blue.subtlest')} !important`,
	},

	'td[colorname="light teal" i], th[colorname="light teal" i]': {
		backgroundColor: `${token('color.background.accent.teal.subtlest')} !important`,
	},

	'td[colorname="light green" i], th[colorname="light green" i]': {
		backgroundColor: `${token('color.background.accent.green.subtlest')} !important`,
	},

	'td[colorname="light yellow" i], th[colorname="light yellow" i]': {
		backgroundColor: `${token('color.background.accent.yellow.subtlest')} !important`,
	},

	'td[colorname="light red" i], th[colorname="light red" i]': {
		backgroundColor: `${token('color.background.accent.red.subtlest')} !important`,
	},

	'td[colorname="light purple" i], th[colorname="light purple" i]': {
		backgroundColor: `${token('color.background.accent.purple.subtlest')} !important`,
	},

	'td[colorname="light gray" i], th[colorname="light gray" i]': {
		backgroundColor: `${token('color.background.accent.gray.subtlest')} !important`,
	},

	'td[colorname="blue" i], th[colorname="blue" i]': {
		backgroundColor: `${token('color.background.accent.blue.subtler')} !important`,
	},

	'td[colorname="teal" i], th[colorname="teal" i]': {
		backgroundColor: `${token('color.background.accent.teal.subtler')} !important`,
	},

	'td[colorname="green" i], th[colorname="green" i]': {
		backgroundColor: `${token('color.background.accent.green.subtler')} !important`,
	},

	'td[colorname="yellow" i], th[colorname="yellow" i]': {
		backgroundColor: `${token('color.background.accent.yellow.subtler')} !important`,
	},

	'td[colorname="red" i], th[colorname="red" i]': {
		backgroundColor: `${token('color.background.accent.red.subtler')} !important`,
	},

	'td[colorname="purple" i], th[colorname="purple" i]': {
		backgroundColor: `${token('color.background.accent.purple.subtler')} !important`,
	},

	'td[colorname="gray" i], th[colorname="gray" i]': {
		backgroundColor: `${token('color.background.accent.gray.subtle')} !important`,
	},

	'td[colorname="dark blue" i], th[colorname="dark blue" i]': {
		backgroundColor: `${token('color.background.accent.blue.subtle')} !important`,
	},

	'td[colorname="dark teal" i], th[colorname="dark teal" i]': {
		backgroundColor: `${token('color.background.accent.teal.subtle')} !important`,
	},

	'td[colorname="dark green" i], th[colorname="dark green" i]': {
		backgroundColor: `${token('color.background.accent.green.subtle')} !important`,
	},

	'td[colorname="dark yellow" i], th[colorname="dark yellow" i]': {
		backgroundColor: `${token('color.background.accent.orange.subtle')} !important`,
	},

	'td[colorname="dark red" i], th[colorname="dark red" i]': {
		backgroundColor: `${token('color.background.accent.red.subtle')} !important`,
	},

	'td[colorname="dark purple" i], th[colorname="dark purple" i]': {
		backgroundColor: `${token('color.background.accent.purple.subtle')} !important`,
	},

	[`.${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		position: 'relative',
		marginTop: 0,
		marginRight: 'auto',
		marginBottom: token('space.200'),
		marginLeft: 'auto',
		boxSizing: 'border-box',
		/**
		 * Fix block top alignment inside table cells.
		 */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'.decisionItemView-content-wrap:first-of-type > div': {
			marginTop: 0,
		},
	},
	[`.${TableSharedCssClassName.TABLE_CONTAINER}[data-number-column='true']`]: {
		paddingLeft: `${akEditorTableNumberColumnWidth - 1}px`,
		clear: 'both',
	},

	[`.${TableSharedCssClassName.TABLE_RESIZER_CONTAINER}`]: {
		willChange: 'width, margin-left',
	},

	[`.${TableSharedCssClassName.TABLE_RESIZER_CONTAINER} table`]: {
		willChange: 'width',
	},

	[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table`]: {
		marginTop: token('space.300'),
		marginRight: 0,
		marginBottom: 0,
		marginLeft: 0,
	},

	[`.${TableSharedCssClassName.TABLE_CONTAINER} > table,
	.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		marginTop: token('space.300'),
		marginRight: token('space.100'),
		marginBottom: 0,
		marginLeft: 0,
	},

	/* avoid applying styles to nested tables (possible via extensions) */
	[`.${TableSharedCssClassName.TABLE_CONTAINER} > table,
	.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
	.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		borderCollapse: 'collapse',
		border: `${tableCellBorderWidth}px solid ${token('color.background.accent.gray.subtler')}`,
		tableLayout: 'fixed',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '1em',
		width: '100%',

		'&[data-autosize="true"]': {
			tableLayout: 'auto',
		},

		'&[data-initial-width-mode="content"]': {
			tableLayout: 'auto',
			width: 'auto',
		},

		'*': {
			boxSizing: 'border-box',
		},
		hr: {
			boxSizing: 'content-box',
		},
		tbody: {
			borderBottom: 'none',
		},
		'th td': {
			backgroundColor: token('color.background.neutral.subtle'),
		},
		'> tbody > tr > td': {
			backgroundColor: token('elevation.surface'),
		},
		th: {
			backgroundColor: token('color.background.accent.gray.subtlest'),
			textAlign: 'left',

			/* only apply this styling to codeblocks in default background headercells */
			/* TODO this needs to be overhauled as it relies on unsafe selectors */
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'&:not([style]):not(.danger)': {
				[`.${CodeBlockSharedCssClassName.CODEBLOCK_CONTAINER}:not(.danger)`]: {
					backgroundColor: token('elevation.surface.raised'),

					[`&:not(.${akEditorSelectedNodeClassName})`]: {
						boxShadow: `0px 0px 0px 1px ${token('color.border')}`,
					},

					[`.${CodeBlockSharedCssClassName.CODEBLOCK_CONTENT_WRAPPER}`]: {
						// originally copied from packages/editor/editor-shared-styles/src/overflow-shadow/overflow-shadow.ts
						backgroundImage: `linear-gradient(
							to right,
							${token('color.background.neutral')} ${token('space.300')},
							transparent ${token('space.300')}
						  ),linear-gradient(
							to right,
							${token('elevation.surface.raised')} ${token('space.300')},
							transparent ${token('space.300')}
						  ),linear-gradient(
							to left,
							${token('color.background.neutral')} ${token('space.100')},
							transparent ${token('space.100')}
						  ),linear-gradient(
							to left,
							${token('elevation.surface.raised')} ${token('space.100')},
							transparent ${token('space.100')}
						  ),linear-gradient(
							to left,
							${token('elevation.shadow.overflow.spread')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  ),linear-gradient(
							to left,
							${token('elevation.shadow.overflow.perimeter')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  ),linear-gradient(
							to right,
							${token('elevation.shadow.overflow.spread')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  ),linear-gradient(
							to right,
							${token('elevation.shadow.overflow.perimeter')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  )`,
						backgroundColor: token('color.background.neutral'),
					},

					[`.${CodeBlockSharedCssClassName.CODEBLOCK_LINE_NUMBER_GUTTER}`]: {
						backgroundColor: token('color.background.neutral'),
					},

					/* this is only relevant to the element taken care of by renderer */
					'> [data-ds--code--code-block]': {
						// originally copied from packages/editor/editor-shared-styles/src/overflow-shadow/overflow-shadow.ts
						backgroundImage: `linear-gradient(
							to right,
							${token('color.background.neutral')} ${token('space.300')},
							transparent ${token('space.300')}
						  ),linear-gradient(
							to right,
							${token('elevation.surface.raised')} ${token('space.300')},
							transparent ${token('space.300')}
						  ),linear-gradient(
							to left,
							${token('color.background.neutral')} ${token('space.100')},
							transparent ${token('space.100')}
						  ),linear-gradient(
							to left,
							${token('elevation.surface.raised')} ${token('space.100')},
							transparent ${token('space.100')}
						  ),linear-gradient(
							to left,
							${token('elevation.shadow.overflow.spread')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  ),linear-gradient(
							to left,
							${token('elevation.shadow.overflow.perimeter')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  ),linear-gradient(
							to right,
							${token('elevation.shadow.overflow.spread')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  ),linear-gradient(
							to right,
							${token('elevation.shadow.overflow.perimeter')} 0,
							${token('utility.UNSAFE.transparent')}  ${token('space.100')}
						  )`,

						backgroundColor: `${token('color.background.neutral')}!important`,

						// selector lives inside @atlaskit/code
						'--ds--code--line-number-bg-color': token('color.background.neutral'),
					},
				},
			},
		},
	},
});

const roundedTableRemixBlockHighlightStyles = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER}.${REMIX_BLOCK_HIGHLIGHT_CLASS_NAME} > table,
	.${TableSharedCssClassName.TABLE_CONTAINER}.${REMIX_BLOCK_HIGHLIGHT_CLASS_NAME} > .${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
	.${TableSharedCssClassName.TABLE_CONTAINER}.${REMIX_BLOCK_HIGHLIGHT_CLASS_NAME} > .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
		{
			'&::after': {
				borderColor: token('color.border.selected'),
			},
		},
});

const roundedTableOuterBorderOverlayStyles = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER} > table,
	.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
	.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		borderColor: 'transparent',
		position: 'relative',

		'&::after': {
			content: "''",
			position: 'absolute',
			inset: '-0.5px',
			border: `${tableCellBorderWidth}px solid ${token('color.background.accent.gray.subtler')}`,
			borderRadius: token('radius.xlarge'),
			pointerEvents: 'none',
			zIndex: 1,
		},

		'> tbody > tr > th[data-reaches-top], > tbody > tr > td[data-reaches-top]': {
			borderTopColor: 'transparent',
		},

		'> tbody > tr > th[data-reaches-bottom], > tbody > tr > td[data-reaches-bottom]': {
			borderBottomColor: 'transparent',
		},

		'> tbody > tr > th[data-reaches-left], > tbody > tr > td[data-reaches-left]': {
			borderLeftColor: 'transparent',
		},

		'> tbody > tr > th[data-reaches-right], > tbody > tr > td[data-reaches-right]': {
			borderRightColor: 'transparent',
		},

		'> tbody > tr > th[data-reaches-top][data-reaches-left], > tbody > tr > td[data-reaches-top][data-reaches-left]':
			{
				borderTopLeftRadius: token('radius.xlarge'),
				backgroundClip: 'border-box',
				clipPath: `inset(0 round ${token('radius.xlarge')} 0 0 0)`,
			},

		'> tbody > tr > th[data-reaches-top][data-reaches-right], > tbody > tr > td[data-reaches-top][data-reaches-right]':
			{
				borderTopRightRadius: token('radius.xlarge'),
				backgroundClip: 'border-box',
				clipPath: `inset(0 round 0 ${token('radius.xlarge')} 0 0)`,
			},

		'> tbody > tr > th[data-reaches-bottom][data-reaches-left], > tbody > tr > td[data-reaches-bottom][data-reaches-left]':
			{
				borderBottomLeftRadius: token('radius.xlarge'),
				backgroundClip: 'border-box',
				clipPath: `inset(0 round 0 0 0 ${token('radius.xlarge')})`,
			},

		'> tbody > tr > th[data-reaches-bottom][data-reaches-right], > tbody > tr > td[data-reaches-bottom][data-reaches-right]':
			{
				borderBottomRightRadius: token('radius.xlarge'),
				backgroundClip: 'border-box',
				clipPath: `inset(0 round 0 0 ${token('radius.xlarge')} 0)`,
			},
	},

	[`.${TableSharedCssClassName.TABLE_CONTAINER}.is-sticky > .${TableSharedCssClassName.TABLE_STICKY_WRAPPER}`]:
		{
			borderTopLeftRadius: token('radius.xlarge'),
			borderTopRightRadius: token('radius.xlarge'),
			borderBottomLeftRadius: 0,
			borderBottomRightRadius: 0,
		},

	// Keep the cloned sticky header rounded at the top while preserving a square bottom edge.
	[`.${TableSharedCssClassName.TABLE_CONTAINER}.is-sticky > .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
		{
			'&::after': {
				borderBottomLeftRadius: 0,
				borderBottomRightRadius: 0,
			},

			'> tbody > tr': {
				// The sticky table clone sets a row background locally; force it off so
				// the rounded header cells, not a square row box, own the visible fill.
				background: 'transparent !important',
			},

			'> tbody > tr > th, > tbody > tr > td': {
				backgroundClip: 'border-box',
			},

			'> tbody > tr > th[data-reaches-left], > tbody > tr > td[data-reaches-left]': {
				borderTopLeftRadius: token('radius.xlarge'),
				borderBottomLeftRadius: 0,
				clipPath: `inset(0 round ${token('radius.xlarge')} 0 0 0)`,
			},

			'> tbody > tr > th[data-reaches-right], > tbody > tr > td[data-reaches-right]': {
				borderTopRightRadius: token('radius.xlarge'),
				borderBottomRightRadius: 0,
				clipPath: `inset(0 round 0 ${token('radius.xlarge')} 0 0)`,
			},

			'> tbody > tr > th[data-reaches-left][data-reaches-bottom], > tbody > tr > td[data-reaches-left][data-reaches-bottom]':
				{
					borderBottomLeftRadius: 0,
				},

			'> tbody > tr > th[data-reaches-right][data-reaches-bottom], > tbody > tr > td[data-reaches-right][data-reaches-bottom]':
				{
					borderBottomRightRadius: 0,
				},

			'> tbody > tr > th[data-reaches-left]::after, > tbody > tr > td[data-reaches-left]::after': {
				borderBottomLeftRadius: 0,
			},

			'> tbody > tr > th[data-reaches-right]::after, > tbody > tr > td[data-reaches-right]::after':
				{
					borderBottomRightRadius: 0,
				},
		},
});

const tableContentModeStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}:has(table[data-initial-width-mode="content"])`]:
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			width: 'max-content !important',
			maxWidth: 'var(--renderer-table-max-width)',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`.${RendererCssClassName.DOCUMENT} table[data-initial-width-mode="content"] > colgroup > col`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		width: 'unset !important',
	},
});

// Gated behind `platform_editor_table_renderer_fix_2`. Scopes `:has()` to the
// container's OWN table (direct child) so a nested content-mode table (e.g. inside
// an Excerpt macro) no longer blows out its non-content ancestor container.
const tableContentModeScopedStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}:has(> table[data-initial-width-mode="content"]),
	.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}:has(> .${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table[data-initial-width-mode="content"]),
	.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}:has(> .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table[data-initial-width-mode="content"])`]:
		{
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
			width: 'max-content !important',
			maxWidth: 'var(--renderer-table-max-width)',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`.${RendererCssClassName.DOCUMENT} table[data-initial-width-mode="content"] > colgroup > col`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
		width: 'unset !important',
	},
});

const tableContentModeNestedTableStyles = css({
	// Nested tables may sit inside expands, extensions, or other blocks within the parent cell.
	[`.${RendererCssClassName.DOCUMENT} table[data-initial-width-mode="content"] > tbody > tr > :is(th, td) .${TableSharedCssClassName.TABLE_CONTAINER} > table,
		.${RendererCssClassName.DOCUMENT} table[data-initial-width-mode="content"] > tbody > tr > :is(th, td) .${TableSharedCssClassName.TABLE_CONTAINER} > .${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
		.${RendererCssClassName.DOCUMENT} table[data-initial-width-mode="content"] > tbody > tr > :is(th, td) .${TableSharedCssClassName.TABLE_CONTAINER} > .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
		{
			width: 'max-content !important',
			maxWidth: '100%',
		},
});

const tableRendererHeaderStylesForTableCellOnly = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER} > table,
		.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
		.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		'> tbody > tr > th, > tbody > tr > td': {
			minWidth: `${tableCellMinWidth}px`,
			fontWeight: token('font.weight.regular'),
			verticalAlign: 'top',
			border: `${token('border.width')} solid ${token('color.background.accent.gray.subtler')}`,
			borderRightWidth: 0,
			borderBottomWidth: 0,
			paddingTop: token('space.100'),
			paddingRight: token('space.100'),
			paddingBottom: token('space.100'),
			paddingLeft: token('space.100'),
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'th p:not(:first-of-type), td p:not(:first-of-type)': {
				marginTop: token('space.150'),
			},
		},
	},
});

const tableRendererNestedPanelStyles = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER} .ak-editor-panel`]: {
		border: `${token('border.width')} solid ${token('color.border')}`,
	},
});

const tableStylesBackGroundClipForGeckoForTableCellOnly = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER} > table,
		.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
		.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> tbody > tr > th, > tbody > tr > td': {
			backgroundClip: 'padding-box',
		},
	},
});

const firstNodeWithNotMarginTopWithNestedDnD = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER} > table,
		.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
		.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		'> tbody > tr > th, > tbody > tr > td': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'> :nth-child(1 of :not(style, .ProseMirror-gapcursor, .ProseMirror-widget, span))': {
				marginTop: 0,
			},
		},
	},
});

const firstNodeWithNotMarginTop = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER} > table,
		.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
		.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		'> tbody > tr > th, > tbody > tr > td': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'> :first-child:not(style), > style:first-child + *': {
				marginTop: 0,
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'> .ProseMirror-gapcursor:first-child + *, > style:first-child + .ProseMirror-gapcursor + *':
				{
					marginTop: 0,
				},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'> .ProseMirror-gapcursor:first-child + span + *, > style:first-child + .ProseMirror-gapcursor + span + *':
				{
					marginTop: 0,
				},
		},
	},
});

const rendererTableStyles = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		zIndex: 0,
		transition: `all 0.1s linear`,
		display: 'flex' /* needed to avoid position: fixed jumpiness in Chrome */,

		/** Shadow overrides */
		[`&.${shadowClassNames.RIGHT_SHADOW}::after, &.${shadowClassNames.LEFT_SHADOW}::before`]: {
			top: `${tableMarginTop - 1}px`,
			height: `calc(100% - ${tableMarginTop}px)`,
			zIndex: `${akEditorStickyHeaderZIndex}`,
			width: `${tableShadowWidth}px`,
			background: `linear-gradient(
					to left,
					transparent 0,
					${token('elevation.shadow.overflow.spread')} 140%
				),
				linear-gradient(
					to right,
					${token('elevation.shadow.overflow.perimeter')} 0px,
					transparent 1px
				)`,
		},

		[`&.${shadowClassNames.RIGHT_SHADOW}::after`]: {
			background: `linear-gradient(
					to right,
					transparent 0,
					${token('elevation.shadow.overflow.spread')} 140%
				),
				linear-gradient(
					to left,
					${token('elevation.shadow.overflow.perimeter')} 0px,
					transparent 1px
				)`,
			right: `0px`,
		},

		[`&
          .${shadowObserverClassNames.SENTINEL_LEFT},
          &
          .${shadowObserverClassNames.SENTINEL_RIGHT}`]: {
			height: `calc(100% - ${tableMarginTop}px)`,
		},
	},
});

const stickyScrollbarStyles = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		position: 'relative',
		flexDirection: 'column',

		[`> .${TableSharedCssClassName.TABLE_STICKY_SCROLLBAR_CONTAINER}`]: {
			width: '100%',
			display: 'block',
			visibility: 'hidden',
			overflowX: 'auto',
			position: 'sticky',
			bottom: `${token('space.0')}`,
			zIndex: 1,
		},

		[`> .${TableSharedCssClassName.TABLE_STICKY_SCROLLBAR_SENTINEL_BOTTOM},
		> .${TableSharedCssClassName.TABLE_STICKY_SCROLLBAR_SENTINEL_TOP}`]: {
			position: 'absolute',
			width: '100%',
			height: '1px',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
			marginTop: '-1px',
			// need this to avoid sentinel being focused via keyboard
			// this still allows it to be detected by intersection observer
			visibility: 'hidden',
		},
		[`> .${TableSharedCssClassName.TABLE_STICKY_SCROLLBAR_SENTINEL_TOP}`]: {
			top: `${tableRowHeight * 3}px`,
		},
		[`> .${TableSharedCssClassName.TABLE_STICKY_SCROLLBAR_SENTINEL_BOTTOM}`]: {
			bottom: `${token('space.250')}`, // MAX_BROWSER_SCROLLBAR_HEIGHT = 20;
		},
	},
});

const rendererTableHeaderEqualHeightStylesForTableCellOnly = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table, .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
			{
				height: '1px' /* will be ignored */,
				marginLeft: 0,
				marginRight: 0,
			},
	},
	[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table > tbody > tr:first-of-type, .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table > tbody > tr:first-of-type`]:
		{
			height: '100%',
			'td, th': {
				position: 'relative',
			},
		},
});

const rendererTableSortableColumnStyles = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table, .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
			{
				// allow nested heading links
				[`.${RendererCssClassName.SORTABLE_COLUMN_WRAPPER}`]: {
					padding: 0,

					[`.${RendererCssClassName.SORTABLE_COLUMN}`]: {
						width: '100%',
						height: '100%',
						padding: `${tableCellPadding}px`,
						// eslint-disable-next-line @atlaskit/design-system/use-tokens-shape
						borderWidth: '1.5px',
						borderStyle: 'solid',
						borderColor: `transparent`,

						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
						'> *:first-child': {
							marginTop: 0,
						},

						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
						'> .ProseMirror-gapcursor:first-child + *, > style:first-child + .ProseMirror-gapcursor + *':
							{
								marginTop: 0,
							},

						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
						'> .ProseMirror-gapcursor:first-child + span + *, > style:first-child + .ProseMirror-gapcursor + span + *':
							{
								marginTop: 0,
							},

						'@supports selector(:focus-visible)': {
							'&:focus': {
								outline: 'unset',
							},
							'&:focus-visible': {
								borderColor: `${token('color.border.focused')}`,
							},
						},
					},
					[`> .${RendererCssClassName.SORTABLE_COLUMN} > .${RendererCssClassName.SORTABLE_COLUMN_ICON_WRAPPER}`]:
						{
							margin: 0,
							[`.${SORTABLE_COLUMN_ICON_CLASSNAME}`]: {
								opacity: 1,
								transition: `opacity 0.2s ease-in-out`,
							},
						},

					[`> .${RendererCssClassName.SORTABLE_COLUMN}
						> .${RendererCssClassName.SORTABLE_COLUMN_NO_ORDER}`]: {
						[`.${SORTABLE_COLUMN_ICON_CLASSNAME}`]: {
							opacity: 0,
							'&:focus': {
								opacity: 1,
							},
						},
					},

					[`&:hover:not(
							:has(
									.${RendererCssClassName.SORTABLE_COLUMN_WRAPPER}
										.${RendererCssClassName.SORTABLE_COLUMN}:hover
								)
						)
						> .${RendererCssClassName.SORTABLE_COLUMN}
						> .${RendererCssClassName.SORTABLE_COLUMN_NO_ORDER}`]: {
						[`.${SORTABLE_COLUMN_ICON_CLASSNAME}`]: {
							opacity: 1,
						},
					},
				},
			},
	},
});

// Sortable header cells render content inside an extra div, so mirror the th alignment there.
const rendererTableSortableColumnValignStyles = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table, .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
			{
				[`th[data-valign='middle'].${RendererCssClassName.SORTABLE_COLUMN_WRAPPER} > .${RendererCssClassName.SORTABLE_COLUMN}`]:
					{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
					},
				[`th[data-valign='bottom'].${RendererCssClassName.SORTABLE_COLUMN_WRAPPER} > .${RendererCssClassName.SORTABLE_COLUMN}`]:
					{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'flex-end',
					},
			},
	},
});

const rendererTableColumnStyles = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		'table[data-number-column="true"]': {
			[`.${RendererCssClassName.NUMBER_COLUMN}`]: {
				backgroundColor: `${token('color.background.accent.gray.subtlest')}`,
				borderRight: `${token('border.width')} solid
								${token('color.background.accent.gray.subtler')}`,
				width: `${akEditorTableNumberColumnWidth}px`,
				textAlign: 'center',
				color: `${token('color.text.subtlest')}`,
				// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
				fontSize: `${14 / 16} rem`,
			},

			[`.fixed .${RendererCssClassName.NUMBER_COLUMN}`]: {
				borderRight: `0px none`,
			},
		},
	},
});

const rendererTableHeaderEqualHeightStylesAllowNestedHeaderLinksDuplicateAnchor = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table, .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
			{
				[`.${RendererCssClassName.SORTABLE_COLUMN_WRAPPER}`]: {
					[`.${RendererCssClassName.SORTABLE_COLUMN}`]: {
						[`.${HeadingAnchorWrapperClassName}`]: {
							position: 'unset',
						},
						'>': {
							'h1, h2, h3, h4, h5, h6': {
								// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
								marginRight: '30px',
							},
						},
					},
				},
			},
	},
});

const rendererTableHeaderEqualHeightStylesAllowNestedHeaderLinks = css({
	[`.${RendererCssClassName.DOCUMENT} .${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table, .${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]:
			{
				[`.${RendererCssClassName.SORTABLE_COLUMN_WRAPPER}`]: {
					[`.${RendererCssClassName.SORTABLE_COLUMN}`]: {
						[`.${HeadingAnchorWrapperClassName}`]: {
							position: 'unset',
						},
						'> .renderer-heading-wrapper': {
							// Reserve space for the sortable-column icon so the heading+anchor wrapper never overlaps it.
							// Use padding (instead of margin on the heading) so we don't create a gap between the heading
							// text and the copy-link anchor button
							// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
							paddingRight: '30px',
						},
					},
				},
			},
	},
});

// @ts-expect-error - throw TS error when use !import in position.
const stickyHeaderStyles = css({
	'tr[data-header-row].fixed': {
		position: 'fixed !important',
		display: 'flex',
		overflow: 'hidden',
		zIndex: `${akEditorStickyHeaderZIndex}`,

		borderRight: `${token('border.width')} solid ${token('color.background.accent.gray.subtler')}`,
		borderBottom: `${token('border.width')} solid ${token('color.background.accent.gray.subtler')}`,

		/* this is to compensate for the table border */
		transform: 'translateX(-1px)',
	},

	'.sticky > th': {
		zIndex: `${akEditorStickyHeaderZIndex}`,
		position: 'fixed !important',
		top: 0,
	},

	/* Make the number column header sticky */
	'.sticky > td': {
		position: 'fixed !important',
		top: 0,
	},

	/* add border for position: sticky
 and work around background-clip: padding-box
 bug for FF causing box-shadow bug in Chrome */
	'.sticky th, .sticky td': {
		boxShadow: `0px 1px ${token('color.background.accent.gray.subtler')},
			0px -0.5px ${token('color.background.accent.gray.subtler')},
			inset -1px 0px ${token('color.background.accent.gray.subtler')},
			0px -1px ${token('color.background.accent.gray.subtler')}`,
	},

	/* this will remove jumpiness caused in Chrome for sticky headers */
	'.fixed + tr': {
		minHeight: '0px',
	},
});

const codeBlockAndLayoutStyles = css({
	/*
	 * We wrap CodeBlock in a grid to prevent it from overflowing the container of the renderer.
	 * See ED-4159.
	 */
	'& .code-block': {
		maxWidth: '100%',
		display: 'block',
		position: 'relative',
		borderRadius: token('radius.small', '3px'),

		/*
		 * The overall renderer has word-wrap: break; which causes issues with
		 * code block line numbers in Safari / iOS.
		 */
		wordWrap: 'normal',
	},

	'& .MediaGroup, & .code-block': {
		marginTop: `${blockNodesVerticalMargin}`,

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:first-child': {
			marginTop: 0,
		},
	},

	// We overwrite the rule that clears margin-top for first nested codeblocks, as
	// our lightweight codeblock dom structure will always nest the codeblock inside
	// an extra container div which would constantly be targeted. Now, top-level
	// lightweight codeblock containers will not be targeted.
	// NOTE: This must be added after other .code-block styles in the root
	// Renderer stylesheet.
	[`.${RendererCssClassName.DOCUMENT}
		> .${LightWeightCodeBlockCssClassName.CONTAINER}
		.${CodeBlockSharedCssClassName.CODEBLOCK_CONTAINER}`]: {
		marginTop: blockNodesVerticalMargin,
	},

	'& [data-layout-section]': {
		marginTop: token('space.250'),
		'& > div + div': {
			marginLeft: token('space.400'),
		},

		[`@media screen and (max-width: ${gridMediumMaxWidth}px)`]: {
			'& > div + div': {
				marginLeft: 0,
			},
		},

		'& .MediaGroup, & .code-block': {
			marginTop: blockNodesVerticalMargin,

			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
			'&:first-child': {
				marginTop: 0,
			},
		},
	},

	'& li': {
		'> .code-block': {
			margin: `${blockNodesVerticalMargin} 0 0 0`,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> .code-block:first-child': {
			marginTop: 0,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'> div:last-of-type.code-block': {
			marginBottom: blockNodesVerticalMargin,
		},
	},
});

const layoutSectionForAdvancedLayoutsStyles = css({
	'& [data-layout-section]': {
		'& > div + div': {
			marginLeft: 0,
		},
	},
});

const gridRenderForCodeBlockStyles = css({
	'& .code-block': {
		// removed old -ms- prefix, as grid is well supported for MS Edge
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr)',

		'& > span': {
			gridColumn: 1,
		},
	},
});

// This prevents https://product-fabric.atlassian.net/browse/ED-20924
const codeBlockInListSafariFixStyles = css({
	[`&:not([data-node-type='decisionList']) > li,
		&:not(.${SmartCardSharedCssClassName.BLOCK_CARD_CONTAINER}) > li`]: {
		'&::before': {
			content: '" "',
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			lineHeight: akEditorLineHeight,
		},

		[`> p:first-child,
			> .code-block:first-child,
			> .ProseMirror-gapcursor:first-child + .code-block`]: {
			marginTop: `-${akEditorLineHeight}em !important`,
		},
	},
});

const columnLayoutSharedStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'[data-layout-section]': {
		position: 'relative',
		display: 'flex',
		flexDirection: 'row',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > *': {
			flex: 1,
			minWidth: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > .unsupportedBlockView-content-wrap': {
			minWidth: 'initial',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		[`@media screen and (max-width: ${gridMediumMaxWidth}px)`]: {
			flexDirection: 'column',
		},
	},
});

const columnLayoutResponsiveSharedStyle = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-layout-section]': {
		display: 'flex',
		flexDirection: 'row',
		gap: token('space.100'),

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > *': {
			flex: 1,
			minWidth: 0,
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'& > .unsupportedBlockView-content-wrap': {
			minWidth: 'initial',
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.layout-section-container': {
		containerType: 'inline-size',
		containerName: 'layout-area',
	},
});

const columnLayoutResponsiveRendererStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.layout-section-container [data-layout-section]': {
		gap: token('space.600'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-container-queries
		[`@container layout-area (max-width: ${LAYOUT_BREAKPOINT_RENDERER}px)`]: {
			flexDirection: 'column',
			gap: token('space.400'),
		},
	},
});

const rendererAnnotationStylesOld = css({
	"& [data-mark-type='annotation'][data-mark-annotation-state='active'] [data-annotation-mark], & [data-annotation-draft-mark][data-annotation-inline-node]":
		{
			background: token('color.background.accent.yellow.subtler'),
			borderBottom: `${token('border.width.selected')} solid ${token(
				'color.border.accent.yellow',
			)}`,
			boxShadow: token('elevation.shadow.overlay'),
			cursor: 'pointer',
			paddingTop: token('space.050'),
			paddingRight: token('space.025'),
			paddingBottom: token('space.050'),
			paddingLeft: token('space.025'),
		},
});

const rendererAnnotationStyles = css({
	"& [data-mark-type='annotation'][data-mark-annotation-state='active'] [data-annotation-mark]": {
		background: token('color.background.accent.yellow.subtlest'),
		borderBottom: `${token('border.width.selected')} solid ${token('color.border.accent.yellow')}`,
		cursor: 'pointer',
		paddingTop: token('space.050'),
		paddingRight: token('space.025'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.025'),
	},
	"& [data-mark-type='annotation'][data-mark-annotation-state='active'][data-has-focus='true'] [data-annotation-mark]":
		{
			background: token('color.background.accent.yellow.subtlest.pressed'),
			boxShadow: token('elevation.shadow.overlay'),
		},
});

const rendererAnnotationStylesCommentHeightFix = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [data-annotation-draft-mark][data-annotation-inline-node]': {
		borderBottom: `${token('border.width.selected')} solid transparent`,
		cursor: 'pointer',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		paddingTop: '1px',
		paddingRight: 0,
		paddingBottom: token('space.025'),
		paddingLeft: 0,
		backgroundColor: token('color.background.accent.yellow.subtler'),
		borderBottomColor: token('color.border.accent.yellow'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [data-annotation-draft-mark][data-annotation-inline-node][data-inline-card]': {
		padding: '5px 0 3px 0',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& [data-annotation-draft-mark][data-annotation-inline-node].date-lozenger-container': {
		paddingTop: token('space.025'),
	},
});

const RENDERER_LIST_DENSE_GAP = `max(0px,calc((var(--ak-renderer-base-font-size) - ${akEditorFullPageDenseFontSize}px)* (4/3)))`;
const TASKLIST_CONTAINER_DENSE_MARGIN = `max(calc(10px + (var(--ak-renderer-base-font-size) - ${akEditorFullPageDenseFontSize}px) *( 2 / 3)))`;

const scaledDenseEmojiStyles = css({
	[`.${EmojiSharedCssClassName.EMOJI_IMAGE}`]: {
		height: `${defaultDenseEmojiHeight}px`,
		minHeight: `${defaultDenseEmojiHeight}px`,
		minWidth: `${defaultDenseEmojiHeight}px`,
		maxHeight: `${denseEmojiHeightH1}px`,
		maxWidth: `${denseEmojiHeightH1}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		img: {
			width: '100%',
			height: '100%',
			objectFit: 'contain',
		},
	},
	[`h1 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${denseEmojiHeightH1}px`,
			width: `${denseEmojiHeightH1}px`,
		},
	[`h2 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${denseEmojiHeightH2}px`,
			width: `${denseEmojiHeightH2}px`,
		},
	[`h3 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${denseEmojiHeightH3}px`,
			width: `${denseEmojiHeightH3}px`,
		},
	[`h4 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${denseEmojiHeightH4}px`,
			width: `${denseEmojiHeightH4}px`,
		},
	[`:is(h5, h6, p) :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${defaultDenseEmojiHeight}px`,
			width: `${defaultDenseEmojiHeight}px`,
		},
	// sync with platform/packages/linking-platform/smart-card/src/view/InlineCard/IconAndTitleLayout/index.tsx
	[`[data-testid="icon-wrapper"] :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			minHeight: `${defaultInlineEmojiHeight}px`,
			minWidth: `${defaultInlineEmojiHeight}px`,
			height: `${defaultInlineEmojiHeight}px`,
			width: `${defaultInlineEmojiHeight}px`,
		},
});

const scaledDenseUnicodeEmojiStylesNew = css({
	[`[data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${defaultDenseEmojiHeight}px`,
	},
	[`h1 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${denseEmojiHeightH1}px`,
	},
	[`h2 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${denseEmojiHeightH2}px`,
	},
	[`h3 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${denseEmojiHeightH3}px`,
	},
	[`h4 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${denseEmojiHeightH4}px`,
	},
	[`:is(h5, h6, p) [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${defaultDenseEmojiHeight}px`,
	},
});

const scaledEmojiStyles = css({
	[`.${EmojiSharedCssClassName.EMOJI_IMAGE}`]: {
		height: `${defaultEmojiHeight}px`,
		minHeight: `${defaultEmojiHeight}px`,
		minWidth: `${defaultEmojiHeight}px`,
		maxHeight: `${scaledEmojiHeightH1}px`,
		maxWidth: `${scaledEmojiHeightH1}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		img: {
			width: '100%',
			height: '100%',
			objectFit: 'contain',
		},
	},
	[`h1 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${scaledEmojiHeightH1}px`,
			width: `${scaledEmojiHeightH1}px`,
		},
	[`h2 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${scaledEmojiHeightH2}px`,
			width: `${scaledEmojiHeightH2}px`,
		},
	[`h3 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${scaledEmojiHeightH3}px`,
			width: `${scaledEmojiHeightH3}px`,
		},
	[`h4 :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${scaledEmojiHeightH4}px`,
			width: `${scaledEmojiHeightH4}px`,
		},
	[`:is(h5, h6, p) :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			height: `${defaultEmojiHeight}px`,
			width: `${defaultEmojiHeight}px`,
		},
	// sync with platform/packages/linking-platform/smart-card/src/view/InlineCard/IconAndTitleLayout/index.tsx
	[`[data-testid="icon-wrapper"] :is(.${EmojiSharedCssClassName.EMOJI_IMAGE}, .${EmojiSharedCssClassName.EMOJI_SPRITE}, .${EmojiSharedCssClassName.EMOJI_PLACEHOLDER})`]:
		{
			minHeight: `${defaultInlineEmojiHeight}px`,
			minWidth: `${defaultInlineEmojiHeight}px`,
			height: `${defaultInlineEmojiHeight}px`,
			width: `${defaultInlineEmojiHeight}px`,
		},
});

const scaledUnicodeEmojiStylesNew = css({
	[`[data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${defaultEmojiHeight}px`,
	},
	[`h1 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${scaledEmojiHeightH1}px`,
	},
	[`h2 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${scaledEmojiHeightH2}px`,
	},
	[`h3 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${scaledEmojiHeightH3}px`,
	},
	[`h4 [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${scaledEmojiHeightH4}px`,
	},
	[`:is(h5, h6, p) [data-emoji-type="unicode"]`]: {
		'--emoji-common-unicode-size': `${defaultEmojiHeight}px`,
	},
});

const denseStyles = css({
	// Scale emoji size based on base font size
	// Default: 20px emoji at 16px base font
	// Scaled: 20px * (baseFontSize/16)
	// E.g., dense mode (13px base): 20px * (13/16) = 16.25px
	[`.${EmojiSharedCssClassName.EMOJI_IMAGE}`]: {
		height: `calc(${defaultEmojiHeight} * var(--ak-renderer-base-font-size) / ${akEditorFullPageDefaultFontSize})`,
		width: `calc(${defaultEmojiHeight} * var(--ak-renderer-base-font-size) / ${akEditorFullPageDefaultFontSize})`,
		maxHeight: `calc(${defaultEmojiHeight} * var(--ak-renderer-base-font-size) / ${akEditorFullPageDefaultFontSize})`,

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		img: {
			width: '100%',
			height: '100%',
			objectFit: 'contain',
		},
	},

	// Scale panel icon
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`.${RendererCssClassName.DOCUMENT} .ak-editor-panel .ak-editor-panel__icon`]: {
		height: token('space.250'),
		width: token('space.250'),
	},

	// Condense spacing across lists, tasks and decisions
	[`.${RendererCssClassName.DOCUMENT}`]: {
		// Adjacent list items
		'li + li': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			marginTop: RENDERER_LIST_DENSE_GAP,
		},
		// Nested lists directly under an li (unordered and ordered)
		'li > ul, li > ol, .ak-ul li > ul, .ak-ul li > ol, .ak-ol li > ul, .ak-ol li > ol': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			marginTop: RENDERER_LIST_DENSE_GAP,
		},

		// Task lists
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-task-list-local-id]': {
			// Task lists: container top margin
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			marginTop: TASKLIST_CONTAINER_DENSE_MARGIN,
		},

		// Task lists: sibling items and nested lists
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-task-list-local-id] > * + *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
			marginTop: RENDERER_LIST_DENSE_GAP,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-task-list-local-id] [data-task-list-local-id], [data-task-local-id] [data-task-local-id]':
			{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
				marginTop: RENDERER_LIST_DENSE_GAP,
			},
	},
});

const syncBlockRendererStyles = css({
	margin: 0,
	maxWidth: 'none',
	padding: 0,
});

/**
 * Fake left/right table borders.
 *
 * Adapted from the editor-plugin-table behavior (see editor-common's
 * `tableSharedStyle()` in `editor-common/src/styles/shared/table.ts`). The
 * visible vertical edges of the table are painted by these absolutely-positioned
 * 1px divs that live inside the (non-scrolling) TABLE_CONTAINER, while the real
 * <table> left/right borders are made `transparent`. Because the divs are not
 * inside the horizontally scrolling TABLE_NODE_WRAPPER, they remain visible
 * regardless of scroll position — fixing the missing right border when an
 * overflowing table inside a reference synced block is scrolled all the way to
 * the left.
 *
 * The `height` formula adjusted for table in renderer
 */
const tableFakeBorderStyles = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		// Mirror the editor's tableSharedStyle() rule from
		// `editor-common/src/styles/shared/table.ts` (lines 142-150). The fake
		// borders are absolutely positioned inside pm-table-container;
		// their height is adjusted for renderer to `100% - space.300 - space.250` so the border runs from
		// the top of the table to the bottom of the container
		[`.${TableSharedCssClassName.TABLE_RIGHT_BORDER},
		.${TableSharedCssClassName.TABLE_LEFT_BORDER}`]: {
			display: 'block',
			width: '1px',
			height: `calc(100% - ${token('space.300')} - ${token('space.250')})`,
			background: token('color.background.accent.gray.subtler'),
			position: 'absolute',
			top: token('space.300'),
		},
		[`.${TableSharedCssClassName.TABLE_RIGHT_BORDER}`]: {
			right: 0,
		},
		[`.${TableSharedCssClassName.TABLE_LEFT_BORDER},
		.${TableSharedCssClassName.TABLE_LEFT_BORDER}[data-with-numbered-table='true']`]: {
			left: 0,
		},
	},
	// Make the real left/right table borders transparent so the fake border
	// divs above are the only painted vertical edges. Top/bottom borders on the
	// <table> element remain visible.
	[`.${TableSharedCssClassName.TABLE_NODE_WRAPPER} > table,
	.${TableSharedCssClassName.TABLE_STICKY_WRAPPER} > table`]: {
		borderLeftColor: 'transparent',
		borderRightColor: 'transparent',
	},
});

const roundedTableFakeBorderOverlayStyles = css({
	[`.${TableSharedCssClassName.TABLE_CONTAINER}`]: {
		[`.${TableSharedCssClassName.TABLE_RIGHT_BORDER},
		.${TableSharedCssClassName.TABLE_LEFT_BORDER}`]: {
			display: 'none',
		},
	},
});

const syncBlockStyles = css({
	[`.${SyncBlockSharedCssClassName.renderer}, .${BodiedSyncBlockSharedCssClassName.renderer}, .${SyncBlockSharedCssClassName.error}, .${SyncBlockSharedCssClassName.loading}`]:
		{
			borderRadius: token('radius.small', '3px'),
			marginTop: token('space.075'),
			paddingBlock: token('space.150'),
			overflow: 'visible',
		},

	[`.${SyncBlockSharedCssClassName.renderer}`]: {
		// First child node in syncBlock in editor is overridden with marginTop: 0, hence apply the same style in renderer for consistency
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'.ak-renderer-document > :first-child': {
			marginTop: 0,
		},
	},
	[`.${BodiedSyncBlockSharedCssClassName.renderer}`]: {
		// First child node in bodiedSyncBlock in editor is overridden with marginTop: 0, hence apply the same style in renderer for consistency
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors
		'> :first-child': {
			marginTop: 0,
		},
	},
	[`.${SyncBlockSharedCssClassName.error}`]: {
		backgroundColor: token('color.background.disabled'),
	},

	[`.${SyncBlockSharedCssClassName.loading}`]: {
		boxShadow: `0 0 0 1px ${token('color.border')}`,
	},
});

type RendererStyleContainerProps = Pick<
	RendererWrapperProps,
	| 'onClick'
	| 'onMouseDown'
	| 'appearance'
	| 'contentMode'
	| 'allowNestedHeaderLinks'
	| 'allowColumnSorting'
	| 'useBlockRenderForCodeBlock'
	| 'allowAnnotations'
	| 'allowTableResizing'
	| 'innerRef'
	| 'children'
	| 'allowRendererContainerStyles'
> & {
	isInsideOfSyncBlock?: boolean;
	isInsideSyncBlock?: boolean;
	testId?: string;
};

export const RendererStyleContainer = (props: RendererStyleContainerProps): jsx.JSX.Element => {
	const {
		onClick,
		onMouseDown,
		appearance,
		contentMode,
		allowNestedHeaderLinks,
		allowColumnSorting,
		useBlockRenderForCodeBlock,
		children,
		innerRef,
		testId,
		isInsideSyncBlock,
	} = props;

	const isAdvancedLayoutsOn = editorExperiment('advanced_layouts', true);
	const isPreviewPanelResponsivenessOn = editorExperiment(
		'platform_editor_preview_panel_responsiveness',
		true,
		{ exposure: true },
	);

	const baseFontSize = getBaseFontSize(appearance, contentMode);
	const browser = getBrowserInfo();
	return (
		<div
			role="none"
			ref={innerRef}
			onClick={onClick}
			onMouseDown={onMouseDown}
			style={
				{
					'--ak-renderer-base-font-size': `${baseFontSize}px`,
					'--ak-renderer-editor-font-heading-h1': `${editorUGCTokensRefreshed['editor.font.heading.h1']}`,
					'--ak-renderer-editor-font-heading-h2': `${editorUGCTokensRefreshed['editor.font.heading.h2']}`,
					'--ak-renderer-editor-font-heading-h3': `${editorUGCTokensRefreshed['editor.font.heading.h3']}`,
					'--ak-renderer-editor-font-heading-h4': `${editorUGCTokensRefreshed['editor.font.heading.h4']}`,
					'--ak-renderer-editor-font-heading-h5': `${editorUGCTokensRefreshed['editor.font.heading.h5']}`,
					'--ak-renderer-editor-font-heading-h6': `${editorUGCTokensRefreshed['editor.font.heading.h6']}`,
					'--ak-renderer-editor-font-normal-text': `${editorUGCTokensRefreshed['editor.font.body']}`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- CSS custom properties for UGC font tokens
					...(expValEquals('platform_editor_small_font_size', 'isEnabled', true) && {
						'--ak-renderer-editor-font-small-text':
							editorUGCTokensRefreshed['editor.font.body.small'],
					}),
				} as React.CSSProperties
			}
			css={[
				expValEquals('confluence_ttvc_inline_extensions', 'isEnabled', true)
					? baseFontStyle
					: originalBaseFontLineHeight,
				baseStyles,
				expValEquals('platform_editor_small_font_size', 'isEnabled', true) && fontSizeStyles,
				expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true)
					? headingAnchorStyles
					: headingAnchorStylesDuplicateAnchor,
				headingAnchorButtonFocusVisibleStyles,
				expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true)
					? hideHeadingCopyLinkWrapperStyles
					: hideHeadingCopyLinkWrapperStylesDuplicateAnchor,
				appearance === 'full-page' &&
					isPreviewPanelResponsivenessOn &&
					rendererFullPageStylesWithReducedPadding,
				appearance === 'full-page' && !isPreviewPanelResponsivenessOn && rendererFullPageStyles,
				appearance === 'full-width' &&
					(expValEquals('platform_editor_remove_important_in_render_ext', 'isEnabled', true)
						? rendererFullWidthStyles
						: oldRendererFullWidthStyles),
				(appearance === 'full-width' ||
					(appearance === 'max' &&
						(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
							expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)))) &&
					!isTableResizingEnabled(appearance) &&
					rendererFullWidthStylesForTableResizing,
				appearance === 'max' &&
					(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
						expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)) &&
					(expValEquals('platform_editor_remove_important_in_render_ext', 'isEnabled', true)
						? rendererMaxWidthStyles
						: oldRendererMaxWidthStyles),

				rovoTelepointerStyles,
				whitespaceSharedStyles,
				blockquoteSharedStyles,
				headingsSharedStyles,
				expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true) &&
					headingWrapperInlineFlowStyles,
				expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true)
					? headingWithAlignmentStyles
					: headingWithAlignmentStylesDuplicateAnchor,
				ruleSharedStyles,
				contentMode === 'compact' && extensionStyle,
				contentMode === 'compact'
					? paragraphStylesUGCScaledMargin
					: paragraphSharedStylesWithEditorUGC,
				listsSharedStyles,
				browser.gecko && listsSharedStylesForGekko,
				expValEquals('platform_editor_flexible_list_schema', 'isEnabled', true) &&
					listItemHiddenMarkerStyles,
				indentationSharedStyles,
				indentationSharedStylesWithMarginFix,
				expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true)
					? blockMarksSharedStyles
					: blockMarksSharedStylesDuplicateAnchor,
				codeMarkSharedStyles,
				shadowSharedStyle,
				dateSharedStyle,
				textColorStyles,
				backgroundColorStyles,
				!expValEquals('platform_editor_lovability_text_bg_color', 'isEnabled', true) &&
					highlightLinksUnsetStyles,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
				textHighlightPaddingStyles,
				tasksAndDecisionsStyles,
				expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true) &&
					headingPanelStyles,
				smartCardStyles,
				smartCardStylesAvatarFix,
				editorExperiment('platform_editor_preview_panel_linking_exp', true) &&
					headerSmartCardStyles,
				smartCardStylesAvatarMarginFix,
				smartCardStylesAvatarListZeroMarginTop,
				fg('editor_inline_comments_on_inline_nodes') &&
					(expValEquals('confluence_fe_renderer_inline_node_mark_color_fix', 'isEnabled', true)
						? rendererAnnotationStyles
						: rendererAnnotationStylesOld),
				// eslint-disable-next-line @atlaskit/platform/no-preconditioning
				fg('editor_inline_comments_on_inline_nodes') && rendererAnnotationStylesCommentHeightFix,
				expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true)
					? baseOtherStyles
					: baseOtherStylesDuplicateAnchor,
				!expValEquals('platform_editor_flex_based_centering', 'isEnabled', true) &&
					extensionCenterAlignLegacyStyles,
				// this should be placed after baseOtherStyles
				expValEquals('platform_editor_render_bodied_extension_as_inline', 'isEnabled', true) &&
					(expValEquals('platform_editor_remove_important_in_render_ext', 'isEnabled', true)
						? extensionAsInlineStyle
						: oldExtensionAsInlineStyle),
				inlineExtensionRendererMarginFix,
				allowNestedHeaderLinks &&
					(expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true)
						? alignedHeadingAnchorStyle
						: alignedHeadingAnchorStyleDuplicateAnchor),
				mediaSingleSharedStyle,
				// merge firstWrappedMediaStyles with mediaSingleSharedStyle when clean up platform_editor_fix_media_in_renderer
				fg('platform_editor_fix_media_in_renderer') && firstWrappedMediaStyles,
				tableSharedStyle,
				expValEquals('platform_editor_table_q4_loveability', 'isEnabled', true) &&
					roundedTableOuterBorderOverlayStyles,
				expValEquals('platform_editor_table_q4_loveability', 'isEnabled', true) &&
					fg('platform_editor_table_q4_patch_1') &&
					roundedTableRemixBlockHighlightStyles,
				expValEquals('platform_editor_table_fit_to_content_auto_convert', 'isEnabled', true) &&
					(fg('platform_editor_table_renderer_fix_2')
						? tableContentModeScopedStyles
						: tableContentModeStyles),
				expValEquals('platform_editor_table_fit_to_content_auto_convert', 'isEnabled', true) &&
					fg('platform_editor_table_nested_renderer_fix') &&
					tableContentModeNestedTableStyles,
				tableRendererHeaderStylesForTableCellOnly,
				fg('platform_editor_bordered_panel_nested_in_table') && tableRendererNestedPanelStyles,
				isBackgroundClipBrowserFixNeeded() && tableStylesBackGroundClipForGeckoForTableCellOnly,
				fg('platform_editor_nested_dnd_styles_changes')
					? firstNodeWithNotMarginTopWithNestedDnD
					: firstNodeWithNotMarginTop,
				rendererTableStyles,
				isStickyScrollbarEnabled(appearance) && stickyScrollbarStyles,
				rendererTableHeaderEqualHeightStylesForTableCellOnly,
				allowColumnSorting && rendererTableSortableColumnStyles,
				allowColumnSorting &&
					expValEqualsNoExposure('platform_editor_table_menu_updates', 'isEnabled', true) &&
					rendererTableSortableColumnValignStyles,
				allowColumnSorting &&
					allowNestedHeaderLinks &&
					(expValEquals('platform_editor_copy_link_a11y_inconsistency_fix', 'isEnabled', true)
						? rendererTableHeaderEqualHeightStylesAllowNestedHeaderLinks
						: rendererTableHeaderEqualHeightStylesAllowNestedHeaderLinksDuplicateAnchor),
				rendererTableColumnStyles,
				stickyHeaderStyles,
				codeBlockAndLayoutStyles,
				columnLayoutSharedStyle,
				isAdvancedLayoutsOn && columnLayoutResponsiveSharedStyle,
				isAdvancedLayoutsOn && columnLayoutResponsiveRendererStyles,
				isAdvancedLayoutsOn && layoutSectionForAdvancedLayoutsStyles,
				!useBlockRenderForCodeBlock && gridRenderForCodeBlockStyles,
				browser.safari && codeBlockInListSafariFixStyles,
				appearance === 'full-page' && !isPreviewPanelResponsivenessOn && responsiveBreakoutWidth,
				appearance === 'full-page' &&
					isPreviewPanelResponsivenessOn &&
					responsiveBreakoutWidthWithReducedPadding,
				(appearance === 'full-width' ||
					(appearance === 'max' &&
						(expValEquals('editor_tinymce_full_width_mode', 'isEnabled', true) ||
							expValEquals('confluence_max_width_content_appearance', 'isEnabled', true)))) &&
					responsiveBreakoutWidthFullWidth,
				expValEquals('platform_editor_lovability_emoji_scaling', 'isEnabled', true)
					? contentMode === 'compact'
						? scaledDenseEmojiStyles
						: scaledEmojiStyles
					: contentMode === 'compact'
						? denseStyles
						: undefined,
				contentMode === 'compact' ? scaledDenseUnicodeEmojiStylesNew : scaledUnicodeEmojiStylesNew,
				editorExperiment('platform_synced_block', true) && syncBlockStyles,
				centerWrapperStyles,
				editorExperiment('platform_synced_block', true) && isInsideSyncBlock
					? syncBlockRendererStyles
					: null,
				isInsideSyncBlock &&
					editorExperiment('platform_synced_block', true) &&
					tableFakeBorderStyles,
				isInsideSyncBlock &&
				editorExperiment('platform_synced_block', true) &&
				expValEquals('platform_editor_table_q4_loveability', 'isEnabled', true)
					? roundedTableFakeBorderOverlayStyles
					: null,
				expValEquals('platform_editor_hide_extension_renderer_support', 'isEnabled', true) &&
					hideExtensionStyles,
			]}
			data-testid={testId}
		>
			{children}
		</div>
	);
};
